/**
 * CareerScreen Component
 *
 * THE CORE GAMEPLAY LOOP - Where players train their Pokemon over 63 turns
 *
 * Features:
 * - Training system (5 stats with energy costs and fail chances)
 * - Energy/Rest system
 * - Random events (50 types) and Hangout events (30 types)
 * - Evolution system with modal
 * - Inspiration triggers at turns 11, 23, 35, 47, 59
 * - Gym battles at turns 12, 24, 36, 48 (4 gyms)
 * - Elite Four gauntlet at turns 60, 61, 62, 63
 * - Ability learning system
 * - Multiple view modes (Training, Battle, Log, Abilities, Learn, Gym)
 * - 4 modal components (Evolution, Inspiration, Pokeclock, Help)
 */

import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, Book, Trophy, Clock, Star, HelpCircle, Home, LogOut } from 'lucide-react';
import { motion } from 'framer-motion';
import { useGame } from '../contexts/GameContext';
import { useCareer } from '../contexts/CareerContext';
import { useTutorial } from '../contexts/TutorialContext';
import { useAuth } from '../contexts/AuthContext';
import TutorialOverlay from '../components/TutorialOverlay';
import TutorialHighlight from '../components/TutorialHighlight';
import { apiUpdateTutorialState } from '../services/apiService';
import {
  generatePokemonSprite,
  getGradeColor,
  getPokemonGrade,
  getAptitudeColor,
  StatIcon,
  sortAptitudesByGrade,
  getBestStrategy,
  getEvolutionFormForGrade
} from '../utils/gameUtils';
import {
  ICONS,
  GAME_CONFIG,
  MOVES,
  POKEMON,
  LEGENDARY_POKEMON,
  SUPPORT_CARDS,
  EVOLUTION_CHAINS,
  EVOLUTION_CONFIG,
  ELITE_FOUR,
  normalizeSupportName,
  getSupportAtLimitBreak
} from '../shared/gameData';
import { getSupportImageWithConfig, getGymLeaderImage } from '../constants/trainerImages';
import { TypeIcon, TypeBadge, TYPE_COLORS } from '../components/TypeIcon';

// ============================================================================
// CONSTANTS
// ============================================================================

// Gym leader move counts by gym index (0-indexed) - must match backend
const GYM_LEADER_MOVE_COUNTS = {
  0: 2,  // Gym Leader 1
  1: 3,  // Gym Leader 2
  2: 4,  // Gym Leader 3
  3: 5,  // Gym Leader 4
  4: 5,  // Gym Leader 5
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate difficulty multiplier based on turn
 * MUST EXACTLY MATCH backend GymLeaderService.js calculateDifficultyMultiplier()
 * - Gym 1 (turn 12): 0.8 multiplier
 * - Gym 2 (turn 24): 1.6 multiplier
 * - Gym 3 (turn 36): 2.4 multiplier
 * - Gym 4 (turn 48): 3.2 multiplier
 * - Turn 60: 4.0 multiplier (Elite Four start)
 */
const calculateDifficultyMultiplier = (turn) => {
  if (turn <= 12) {
    // Linear ramp from 0.8 at turn 1 to 0.8 at turn 12
    const progress = (turn - 1) / 11;
    return 0.8 + 0.00 * progress;
  } else if (turn <= 24) {
    // Linear ramp from 0.8 at turn 12 to 1.6 at turn 24
    const progress = (turn - 12) / 12;
    return 0.8 + 0.8 * progress;
  } else if (turn <= 36) {
    // Linear ramp from 1.6 at turn 24 to 2.4 at turn 36
    const progress = (turn - 24) / 12;
    return 1.6 + 0.8 * progress;
  } else if (turn <= 48) {
    // Linear ramp from 2.4 at turn 36 to 3.2 at turn 48
    const progress = (turn - 36) / 12;
    return 2.4 + 0.8 * progress;
  } else {
    // Continue scaling from 3.2 at turn 48 to 4.0 at turn 60
    const progress = (turn - 48) / 12;
    return 3.2 + 0.8 * progress; // Reaches 4.0 at turn 60
  }
};

// Alias for backwards compatibility (exported for other modules)
// eslint-disable-next-line no-unused-vars
const calculateGymLeaderMultiplier = calculateDifficultyMultiplier;

/**
 * Scale stats based on turn for display purposes
 */
const getScaledStats = (baseStats, turn) => {
  const multiplier = calculateDifficultyMultiplier(turn);
  return {
    HP: Math.floor(baseStats.HP * multiplier),
    Attack: Math.floor(baseStats.Attack * multiplier),
    Defense: Math.floor(baseStats.Defense * multiplier),
    Instinct: Math.floor(baseStats.Instinct * multiplier),
    Speed: Math.floor(baseStats.Speed * multiplier)
  };
};

/**
 * Generate a brief description for a move based on its properties
 */
const getMoveDescription = (move) => {
  const parts = [];

  // Describe cooldown characteristics
  if (move.cooldown === 0) {
    parts.push('Quick strike');
  } else if (move.cooldown >= 6) {
    parts.push('Slow but powerful');
  } else if (move.cooldown >= 4) {
    parts.push('Heavy attack');
  }

  // Describe effect
  if (move.effect) {
    const effect = move.effect;
    switch (effect.type) {
      // Status effects
      case 'burn':
        parts.push(`burns for ${effect.damage || 3}% max HP/tick`);
        break;
      case 'poison':
        parts.push(`poisons for ${effect.damage || 4} dmg/tick`);
        break;
      case 'badly_poison':
        parts.push('badly poisons target');
        break;
      case 'paralyze':
        parts.push('may paralyze');
        break;
      case 'stun':
        parts.push('can stun');
        break;
      case 'confuse':
        parts.push('confuses target');
        break;
      case 'freeze':
        parts.push('may freeze');
        break;
      case 'sleep':
        parts.push('induces sleep');
        break;
      case 'soak':
        parts.push('soaks target');
        break;
      case 'infatuate':
        parts.push('may infatuate');
        break;
      case 'curse':
        parts.push('curses target');
        break;

      // Healing/drain effects
      case 'energize':
        parts.push('restores stamina');
        break;
      case 'drain':
        parts.push('drains HP');
        break;
      case 'drain_sleep':
        parts.push('drains sleeping foe');
        break;
      case 'heal_self':
        parts.push('restores HP');
        break;
      case 'rest':
        parts.push('full heal, user sleeps');
        break;
      case 'regen':
        parts.push('heals over time');
        break;

      // Self-penalty effects
      case 'recoil':
        parts.push('causes recoil');
        break;
      case 'exhaust':
        parts.push('exhausts user');
        break;
      case 'self_ko':
        parts.push('user faints');
        break;
      case 'confuse_self_after':
        parts.push('confuses user after');
        break;

      // Buff effects (self)
      case 'buff_all':
        parts.push('boosts all stats');
        break;
      case 'buff_attack':
        parts.push('boosts Attack');
        break;
      case 'buff_defense':
        parts.push('boosts Defense');
        break;
      case 'buff_speed':
        parts.push('boosts Speed');
        break;
      case 'buff_instinct':
        parts.push('boosts Instinct');
        break;
      case 'buff_crit':
        parts.push('boosts critical rate');
        break;
      case 'buff_attack_defense':
        parts.push('boosts Attack & Defense');
        break;
      case 'buff_attack_speed':
        parts.push('boosts Attack & Speed');
        break;
      case 'evasion':
        parts.push('grants evasion');
        break;

      // Debuff effects (opponent)
      case 'debuff_attack':
        parts.push('lowers foe Attack');
        break;
      case 'debuff_defense':
        parts.push('lowers foe Defense');
        break;
      case 'debuff_speed':
        parts.push('lowers foe Speed');
        break;
      case 'debuff_instinct':
        parts.push('lowers foe Instinct');
        break;
      case 'debuff_accuracy':
        parts.push('lowers foe accuracy');
        break;

      // Self-debuff effects
      case 'debuff_instinct_self':
        parts.push('lowers user Instinct');
        break;
      case 'debuff_speed_self':
        parts.push('lowers user Speed');
        break;
      case 'debuff_attack_self':
        parts.push('lowers user Attack');
        break;

      // Special damage mechanics
      case 'high_crit':
        parts.push('high critical rate');
        break;
      case 'double_if_status':
        parts.push('double vs status');
        break;
      case 'double_if_status_self':
        parts.push('double if user has status');
        break;
      case 'double_if_half_hp':
        parts.push('double if foe <50% HP');
        break;
      case 'hp_based_damage':
        parts.push('power scales with HP');
        break;
      case 'buff_boost_damage':
        parts.push('power scales with buffs');
        break;
      case 'use_opponent_attack':
        parts.push('uses foe Attack stat');
        break;
      case 'ohko':
        parts.push('one-hit KO chance');
        break;
      case 'consecutive_boost':
        parts.push('grows stronger on hit');
        break;
      case 'delayed_damage':
        parts.push('delayed strike');
        break;

      // Field effects
      case 'push_back':
        parts.push('pushes foe back');
        break;
      case 'entry_hazard':
        parts.push('sets entry hazard');
        break;
      case 'entry_hazard_rock':
        parts.push('sets stealth rocks');
        break;
      case 'remove_hazards':
        parts.push('clears hazards');
        break;

      // Weather effects
      case 'weather_sand':
        parts.push('sandstorm (chip damage)');
        break;
      case 'weather_rain':
        parts.push('rain (+50% Water)');
        break;
      case 'weather_sun':
        parts.push('sun (+50% Fire)');
        break;
      case 'weather_hail':
        parts.push('hail (chip damage)');
        break;

      // Terrain effects
      case 'terrain_psychic':
        parts.push('psychic terrain (+50% Psychic)');
        break;
      case 'terrain_electric':
        parts.push('electric terrain (+50% Electric)');
        break;
      case 'terrain_grassy':
        parts.push('grassy terrain (+50% Grass)');
        break;

      // Unique/special effects
      case 'destiny_bond':
        parts.push('foe faints if user does');
        break;
      case 'random_move':
        parts.push('uses random move');
        break;
      case 'random_damage_or_heal':
        parts.push('random damage or heal');
        break;
      case 'copy_opponent':
        parts.push('copies opponent');
        break;
      case 'switch_out':
        parts.push('user switches out');
        break;

      default:
        break;
    }
  }

  // If no special characteristics, describe based on damage
  if (parts.length === 0) {
    if (move.damage === 0) {
      return 'Status move';
    } else if (move.damage >= 35) {
      return 'High-power attack';
    } else if (move.damage >= 25) {
      return 'Solid damage dealer';
    } else if (move.damage >= 15) {
      return 'Balanced attack';
    } else {
      return 'Light attack';
    }
  }

  return parts.join(', ');
};

// NOTE: checkAndApplyInspiration removed - now handled server-side
// Backend applies inspirations in routes/career/progression.js POST /apply-inspiration
// Server uses cached RNG to ensure client and server agree on inspiration results

/**
 * Get support card attributes using the card's actual trainingBonus values
 * Must match backend career.js getSupportCardAttributes
 * @param {string} supportKey - Support card name
 * @param {number} limitBreakLevel - Limit break level (0-4), defaults to 4 (max)
 */
const getSupportCardAttributes = (supportKey, limitBreakLevel = 4) => {
  // Normalize legacy support names to new format
  const normalizedKey = normalizeSupportName(supportKey);

  // Get the limit break adjusted card
  const card = getSupportAtLimitBreak(normalizedKey, limitBreakLevel);
  if (!card) return null;

  // Extract training bonuses from card's trainingBonus object
  const trainingBonus = card.trainingBonus || {};

  return {
    ...card,
    // Map card's trainingBonus to the expected property names
    typeBonusTraining: trainingBonus.typeMatch || 5,
    generalBonusTraining: trainingBonus.otherStats || 1,
    friendshipBonusTraining: trainingBonus.maxFriendshipTypeMatch || 10,
    // Use card's actual appearance settings
    appearanceChance: card.appearanceRate || 0.40,
    typeAppearancePriority: 1 + (card.typeMatchPreference || 0.10) * 10,
    supportType: card.supportType  // This is the stat type (HP, Attack, etc.), NOT the Pokemon type
  };
};

const CareerScreen = () => {
  const {
    setGameState,
    resetGameState,
    selectedInspirations,
    battleState,
    setBattleState
  } = useGame();

  const {
    careerData,
    setCareerData,
    processBattle,
    trainStat,
    restOnServer,
    relaxOnServer,
    generateTraining,
    triggerEvent,
    resolveEvent: resolveEventOnServer,
    clearEventResult,
    learnAbility: learnAbilityOnServer,
    forgetAbility: forgetAbilityOnServer,
    changeStrategy,
    abandonCareer,
    evolve: evolveOnServer,
    applyInspiration: applyInspirationOnServer
  } = useCareer();

  const {
    isTutorialStep,
    advanceStep,
    markStepShown,
    hasShownStep,
    markPokeclockFirstDefeatShown,
    markPokeclockUsageShown
  } = useTutorial();
  const { authToken } = useAuth();

  const [viewMode, setViewMode] = useState('training');
  const [showHelp, setShowHelp] = useState(false);
  const [evolutionModal, setEvolutionModal] = useState(null);
  const [inspirationModal, setInspirationModal] = useState(null);
  const [pokeclockModal] = useState(false); // Legacy - unused but referenced by PokeclockModal component
  const [pendingBattleState, setPendingBattleState] = useState(null); // Holds battle state until modal dismissed
  const [showStrategySelector, setShowStrategySelector] = useState(false);
  const [isProcessingEvent, setIsProcessingEvent] = useState(false);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [forgetMoveConfirm, setForgetMoveConfirm] = useState(null);
  const [showAbandonConfirm, setShowAbandonConfirm] = useState(false);
  const lastProcessedTurnRef = useRef(null);
  const declinedEventRef = useRef(false);
  const prevBattleStateRef = useRef(null);
  const pendingEvolutionCheckRef = useRef(false);

  // Modal queue system - prevents concurrent modals
  const [modalQueue, setModalQueue] = useState([]);
  const [isProcessingModal, setIsProcessingModal] = useState(false);

  // Tutorial state
  const [showTutorialOverlay, setShowTutorialOverlay] = useState(null); // Step ID or null
  const [showTutorialHighlight, setShowTutorialHighlight] = useState(null); // Selector string or null

  // ============================================================================
  // HELPER FUNCTIONS (Component-specific)
  // ============================================================================

  /**
   * Request server to generate new training options (SERVER-AUTHORITATIVE)
   */
  const requestNewTrainingOptions = async () => {
    await generateTraining();
    // Career state will be updated through CareerContext
  };

  /**
   * Check if Pokemon can evolve based on grade
   */
  const checkForEvolution = (pokemonName, currentStats, serverCareerState) => {
    // Use basePokemonName from server state to look up the evolution chain
    const baseName = serverCareerState?.basePokemonName || pokemonName;
    const evolutionChain = EVOLUTION_CHAINS[baseName];
    if (!evolutionChain) return null;

    const currentGrade = getPokemonGrade(currentStats);
    // Use evolution stage from server state (not React state which may be stale)
    const currentStage = serverCareerState?.evolutionStage || 0;
    const totalStats = Object.values(currentStats).reduce((sum, val) => sum + val, 0);

    console.log('[checkForEvolution] Pokemon:', pokemonName, 'Base:', baseName, 'Grade:', currentGrade, 'TotalStats:', totalStats, 'CurrentStage:', currentStage, 'Chain:', evolutionChain);

    // Define grade order for comparison (index-based)
    const gradeOrder = ['F', 'F+', 'E', 'E+', 'D', 'D+', 'C', 'C+', 'B', 'B+', 'A', 'A+', 'S', 'S+', 'UU', 'UU+'];
    const gradeIndex = gradeOrder.indexOf(currentGrade);
    const cGradeIndex = gradeOrder.indexOf('C');  // Index 6
    const aGradeIndex = gradeOrder.indexOf('A');  // Index 10

    // Check for stage 1 evolution (at C grade or higher)
    if (currentStage === 0 && evolutionChain.stage1 && gradeIndex >= cGradeIndex) {
      // Handle array of evolution options (e.g., Eevee) - randomly select one
      const stage1Options = Array.isArray(evolutionChain.stage1) ? evolutionChain.stage1 : [evolutionChain.stage1];
      const selectedEvolution = stage1Options[Math.floor(Math.random() * stage1Options.length)];
      console.log('[checkForEvolution] Triggering stage 1 evolution to:', selectedEvolution, '(from options:', stage1Options, ')');
      return { toName: selectedEvolution, toStage: 1 };
    }

    // Check for stage 2 evolution (at A grade or higher)
    if (currentStage === 1 && evolutionChain.stage2 && gradeIndex >= aGradeIndex) {
      console.log('[checkForEvolution] Triggering stage 2 evolution to:', evolutionChain.stage2);
      return { toName: evolutionChain.stage2, toStage: 2 };
    }

    console.log('[checkForEvolution] No evolution triggered. Stage1 condition:', currentStage === 0 && evolutionChain.stage1, 'gradeIndex:', gradeIndex, 'cGradeIndex:', cGradeIndex, 'Grade:', currentGrade);
    return null;
  };

  /**
   * Helper to check for evolution and trigger modal if needed
   * Call this after ANY stat gain (training, battles, events)
   */
  const checkAndTriggerEvolution = (careerState) => {
    console.log('[checkAndTriggerEvolution] Called with:', {
      hasCareerState: !!careerState,
      hasPokemon: !!careerState?.pokemon,
      hasCurrentStats: !!careerState?.currentStats,
      pokemonName: careerState?.pokemon?.name,
      evolutionStage: careerState?.evolutionStage
    });
    if (!careerState || !careerState.pokemon || !careerState.currentStats) return;

    const evolutionCheck = checkForEvolution(
      careerState.pokemon.name,
      careerState.currentStats,
      careerState
    );

    if (evolutionCheck) {
      const oldStats = careerState.currentStats;
      const statBoost = EVOLUTION_CHAINS[careerState.basePokemonName || careerState.pokemon.name]?.stages === 2
        ? EVOLUTION_CONFIG.STAT_BOOST.TWO_STAGE
        : EVOLUTION_CONFIG.STAT_BOOST.ONE_STAGE;
      const newStats = {};
      for (const [s, value] of Object.entries(oldStats)) {
        newStats[s] = Math.round(value * (1 + statBoost));
      }

      setTimeout(() => {
        enqueueModal('evolution', {
          fromName: careerState.pokemon.name,
          toName: evolutionCheck.toName,
          toStage: evolutionCheck.toStage,
          oldStats: oldStats,
          newStats: newStats
        });
      }, 0);
    }
  };

  /**
   * Apply evolution to Pokemon (SERVER-AUTHORITATIVE)
   */
  const applyEvolution = async (fromName, toName, toStage) => {
    console.log('=== applyEvolution START (server-authoritative) ===');
    console.log('fromName:', fromName, 'toName:', toName, 'toStage:', toStage);

    // Call server to process evolution
    const result = await evolveOnServer(toName);

    if (!result) {
      console.error('Failed to evolve on server');
    } else {
      console.log('Evolution successful:', result.evolution);
    }

    setEvolutionModal(null);
    processNextModal(); // Process next modal in queue
    console.log('=== applyEvolution END ===');
  };

  /**
   * Perform training action (SERVER-AUTHORITATIVE)
   */
  const performTraining = async (stat) => {
    // Prevent double-clicks
    if (isProcessingAction) return;
    setIsProcessingAction(true);

    try {
      // Call server-authoritative training endpoint
      const result = await trainStat(stat);

      if (!result) {
        console.error('Training failed - no result from server');
        return;
      }

      // Handle recovered state (action was already processed or state was stale)
      if (result.recovered) {
        console.log('State recovered after interruption, continuing with synced state');
        return; // State has been synced, UI will update via careerData change
      }

      // Server has already updated careerData through CareerContext
      // Now handle client-side presentation (modals, animations)
      const nextTurn = result.careerState.turn;

      // Server already handles moveHints accumulation and returns updated careerState
      // which is merged via updateCareerFromServer in CareerContext
      // No client-side hint processing needed here

      // Check for inspiration event (SERVER-AUTHORITATIVE)
      const inspirationTurns = [11, 23, 35, 47, 59];
      if (inspirationTurns.includes(nextTurn) && selectedInspirations && selectedInspirations.length > 0) {
        const inspirationResult = await applyInspirationOnServer();
        if (inspirationResult && inspirationResult.inspirationResult) {
          setTimeout(() => {
            enqueueModal('inspiration', inspirationResult.inspirationResult);
          }, 0);
        }
      }

      // Check for evolution after stat gain
      checkAndTriggerEvolution(result.careerState);
    } finally {
      setIsProcessingAction(false);
    }
  };

  /**
   * Perform rest action to restore energy (SERVER-AUTHORITATIVE)
   */
  const performRest = async () => {
    // Prevent double-clicks
    if (isProcessingAction) return;
    setIsProcessingAction(true);

    try {
      // Call server-authoritative rest endpoint
      const result = await restOnServer();

      if (!result) {
        console.error('Rest failed - no result from server');
        return;
      }

      // Handle recovered state (action was already processed or state was stale)
      if (result.recovered) {
        console.log('State recovered after interruption, continuing with synced state');
        return; // State has been synced, UI will update via careerData change
      }

      // Server has already updated careerData through CareerContext
      // Now handle client-side presentation (modals, animations)
      const nextTurn = result.careerState.turn;

      // Check for inspiration event (SERVER-AUTHORITATIVE)
      const inspirationTurns = [11, 23, 35, 47, 59];
      if (inspirationTurns.includes(nextTurn) && selectedInspirations && selectedInspirations.length > 0) {
        const inspirationResult = await applyInspirationOnServer();
        if (inspirationResult && inspirationResult.inspirationResult) {
          enqueueModal('inspiration', inspirationResult.inspirationResult);
        }
      }

      console.log('[performRest] Energy gain:', result.energyGain, 'New energy:', result.careerState.energy);
    } finally {
      // Small delay to prevent rapid clicks
      setTimeout(() => setIsProcessingAction(false), 300);
    }
  };

  /**
   * Perform relax action to improve mood (SERVER-AUTHORITATIVE)
   */
  const performRelax = async () => {
    // Prevent double-clicks
    if (isProcessingAction) return;
    setIsProcessingAction(true);

    try {
      // Call server-authoritative relax endpoint
      const result = await relaxOnServer();

      if (!result) {
        console.error('Relax failed - no result from server');
        return;
      }

      // Handle recovered state (action was already processed or state was stale)
      if (result.recovered) {
        console.log('State recovered after interruption, continuing with synced state');
        return; // State has been synced, UI will update via careerData change
      }

      // Server has already updated careerData through CareerContext
      // Now handle client-side presentation (modals, animations)
      const nextTurn = result.careerState.turn;

      // Check for inspiration event (SERVER-AUTHORITATIVE)
      const inspirationTurns = [11, 23, 35, 47, 59];
      if (inspirationTurns.includes(nextTurn) && selectedInspirations && selectedInspirations.length > 0) {
        const inspirationResult = await applyInspirationOnServer();
        if (inspirationResult && inspirationResult.inspirationResult) {
          enqueueModal('inspiration', inspirationResult.inspirationResult);
        }
      }

      console.log('[performRelax] Mood gain:', result.moodGain, 'Energy gain:', result.energyGain, 'New mood:', result.newMood);
    } finally {
      // Small delay to prevent rapid clicks
      setTimeout(() => setIsProcessingAction(false), 300);
    }
  };

  /**
   * Resolve event by making choice (SERVER-AUTHORITATIVE)
   */
  const handleEventChoice = async (choiceIndex) => {
    // Prevent double-clicks and flickering
    if (isProcessingEvent) return;
    setIsProcessingEvent(true);

    try {
      const result = await resolveEventOnServer(choiceIndex);

      if (!result) {
        console.error('Event resolution failed - no result from server');
        return;
      }

      const { eventResult, careerState } = result;

      // If eventResult contains a moveHint, ensure it's added to learnableAbilities
      // (Server should handle this, but we ensure it client-side as well)
      if (eventResult?.moveHint) {
        console.log('[handleEventChoice] Adding moveHint to learnableAbilities:', eventResult.moveHint);
        setCareerData(prev => {
          // Check if move is already in learnableAbilities or known
          const isAlreadyLearnable = prev.pokemon.learnableAbilities?.includes(eventResult.moveHint);
          const isAlreadyKnown = prev.knownAbilities?.includes(eventResult.moveHint);

          if (isAlreadyLearnable || isAlreadyKnown) {
            console.log('[handleEventChoice] Move already known/learnable, skipping:', eventResult.moveHint);
            return prev; // No update needed
          }

          console.log('[handleEventChoice] Adding to learnableAbilities:', {
            move: eventResult.moveHint,
            currentLearnables: prev.pokemon.learnableAbilities
          });

          // Add moveHint to learnableAbilities
          // Event hints are level 1, so discount is 0.15 (matching server logic)
          const HINT_DISCOUNT = 0.15;
          const MAX_HINT_DISCOUNT = 0.60;
          return {
            ...prev,
            pokemon: {
              ...prev.pokemon,
              learnableAbilities: [...(prev.pokemon.learnableAbilities || []), eventResult.moveHint]
            },
            // Also update moveHints discount value (event hints are level 1 = 0.15)
            moveHints: {
              ...prev.moveHints,
              [eventResult.moveHint]: Math.min(
                (prev.moveHints?.[eventResult.moveHint] || 0) + HINT_DISCOUNT,
                MAX_HINT_DISCOUNT
              )
            }
          };
        });
      }

      // Check for evolution if event granted stat gains
      if (eventResult?.stats && Object.keys(eventResult.stats).length > 0) {
        checkAndTriggerEvolution(careerState);
      }
    } finally {
      setIsProcessingEvent(false);
    }
  };

  /**
   * Start battle with opponent
   */
  const startBattle = async (opponent, isGymLeader = false, isEliteFour = false, isEventBattle = false) => {
    // Prevent wild battles at 0 energy (gym leaders and event battles don't cost energy upfront)
    if (!isGymLeader && !isEliteFour && !isEventBattle && careerData.energy <= 0) {
      return; // Don't start battle
    }

    console.log('[startBattle] Processing server-side battle:', {
      opponent: opponent.name,
      isGymLeader,
      isEliteFour,
      isEventBattle
    });

    // Call server to process battle
    const battleResult = await processBattle(opponent, isGymLeader, isEventBattle);

    if (!battleResult) {
      console.error('[startBattle] Failed to get battle result from server');
      alert('Battle failed to process. Please try again.');
      return;
    }

    console.log('[startBattle] Server returned battle result:', battleResult);
    console.log('[startBattle] showTutorial:', battleResult.showTutorial);

    // Create player and opponent objects for battle replay
    const playerPokemon = {
      name: careerData.pokemon.name,
      primaryType: careerData.pokemon.primaryType,
      stats: { ...careerData.currentStats },
      abilities: careerData.knownAbilities,
      typeAptitudes: careerData.pokemon.typeAptitudes,
      strategy: careerData.pokemon.strategy,
      strategyGrade: careerData.pokemon.strategyGrade
    };

    // Normalize opponent data - use server's scaled stats from battle log
    // The server scales gym leader/Elite Four stats before battle, so we must use those values
    const pokemonData = opponent.pokemon || opponent; // Elite Four has nested pokemon object
    const opponentName = pokemonData.name || opponent.name;
    const opponentType = pokemonData.primaryType || opponent.primaryType;

    // Get the actual stats from the first battle log entry - these are the SERVER's scaled stats
    const battleLog = battleResult.battleLog || [];
    const initialLogEntry = battleLog[0];

    // Use server's maxHp from battle log for opponent stats (these are properly scaled)
    // Fall back to baseStats only if no battle log exists
    const serverOpponentMaxHp = initialLogEntry?.player2?.maxHp;
    const baseOpponentStats = pokemonData.stats || pokemonData.baseStats;

    // If server provided scaled HP, calculate the scale factor and apply to all stats
    let opponentStats;
    if (serverOpponentMaxHp && baseOpponentStats && baseOpponentStats.HP) {
      const scaleFactor = serverOpponentMaxHp / baseOpponentStats.HP;
      opponentStats = {
        HP: serverOpponentMaxHp,
        Attack: Math.floor(baseOpponentStats.Attack * scaleFactor),
        Defense: Math.floor(baseOpponentStats.Defense * scaleFactor),
        Instinct: Math.floor(baseOpponentStats.Instinct * scaleFactor),
        Speed: Math.floor(baseOpponentStats.Speed * scaleFactor)
      };
    } else {
      opponentStats = baseOpponentStats;
    }

    // Set battle state with server's battle log for replay
    // For gym leaders, we need to preserve the gym leader's name separately from the Pokemon's name
    const gymLeaderName = isGymLeader || isEliteFour ? opponent.name : null;

    const calculatedEliteFourIndex = isEliteFour ? (careerData.turn - (GAME_CONFIG.CAREER.ELITE_FOUR_START_TURN || 60)) : null;
    console.log('[CareerScreen] startBattle - Setting battle state:', {
      isEliteFour,
      careerTurn: careerData.turn,
      eliteFourStartTurn: GAME_CONFIG.CAREER.ELITE_FOUR_START_TURN || 60,
      calculatedEliteFourIndex,
      winner: battleResult.winner
    });
    const newBattleState = {
      player: {
        ...playerPokemon,
        currentHP: playerPokemon.stats.HP,
        currentStamina: 0, // Battles start at 0 stamina
        moveStates: {},
        isResting: false,
        statusEffects: []
      },
      opponent: {
        ...opponent,
        name: opponentName,
        gymLeaderName: gymLeaderName, // Preserve gym leader name for portrait display
        primaryType: opponentType,
        stats: opponentStats,
        currentHP: opponentStats?.HP || 100,
        currentStamina: 0, // Battles start at 0 stamina
        moveStates: {},
        isResting: false,
        statusEffects: []
      },
      tick: 0,
      log: battleResult.battleLog || [], // Use server's battle log
      displayLog: [], // Initialize empty display log - will be populated tick-by-tick
      isGymLeader,
      isEliteFour,
      eliteFourIndex: calculatedEliteFourIndex,
      winner: battleResult.winner, // 'player' or 'opponent'
      rewards: battleResult.rewards, // { statGain, skillPoints, energyChange }
      serverResult: true, // Flag to indicate this came from server
      showTutorial: battleResult.showTutorial // 'first_defeat', 'pokeclock_usage', or null
    };

    // If pokeclock tutorial should show, show tutorial overlay BEFORE battle
    if (battleResult.showTutorial) {
      console.log('[startBattle] Pokeclock tutorial detected, showing tutorial:', battleResult.showTutorial);

      // Store pending battle state to transition after tutorial
      setPendingBattleState(newBattleState);

      // Show appropriate tutorial based on type
      if (battleResult.showTutorial === 'first_defeat') {
        setShowTutorialOverlay('pokeclock-first-defeat');
      } else if (battleResult.showTutorial === 'pokeclock_usage') {
        setShowTutorialOverlay('pokeclock-usage');
      }
    } else {
      // No tutorial, proceed directly to battle
      setBattleState(newBattleState);
      setGameState('battle');
    }
  };

  /**
   * Learn a new move (SERVER-AUTHORITATIVE)
   */
  const learnMove = async (moveName) => {
    const success = await learnAbilityOnServer(moveName);

    if (!success) {
      console.error('Failed to learn ability:', moveName);
    }

    // Server has already updated careerData with the new ability and SP cost
  };

  // Pokeclock tutorial removed - will be reimplemented with standard tutorial system

  /**
   * Enqueue a modal to be shown
   * Modals are shown one at a time to prevent overlap
   */
  const enqueueModal = (modalType, modalData) => {
    console.log('[ModalQueue] Enqueuing modal:', modalType, modalData);
    setModalQueue(prev => [...prev, { type: modalType, data: modalData }]);
  };

  /**
   * Process next modal in queue
   * Called when current modal is dismissed
   */
  const processNextModal = () => {
    console.log('[ModalQueue] Processing next modal, queue length:', modalQueue.length);
    setIsProcessingModal(false);

    // Wait a brief moment for tutorial highlights to clear
    setTimeout(() => {
      setModalQueue(prev => {
        if (prev.length === 0) {
          return prev;
        }

        const [nextModal, ...remaining] = prev;
        console.log('[ModalQueue] Showing next modal:', nextModal.type);

        // Show the next modal
        switch (nextModal.type) {
          case 'evolution':
            setEvolutionModal(nextModal.data);
            break;
          case 'inspiration':
            setInspirationModal(nextModal.data);
            break;
          default:
            console.warn('[ModalQueue] Unknown modal type:', nextModal.type);
        }

        setIsProcessingModal(true);
        return remaining;
      });
    }, 300); // Brief delay for tutorial highlights to clear
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Modal queue processing effect
  useEffect(() => {
    // Don't show modals if tutorial highlight is active
    const hasActiveHighlight = showTutorialHighlight !== null;

    if (modalQueue.length > 0 && !isProcessingModal && !evolutionModal && !inspirationModal && !hasActiveHighlight) {
      console.log('[ModalQueue] Auto-processing queue, length:', modalQueue.length);
      processNextModal();
    }
  }, [modalQueue, isProcessingModal, evolutionModal, inspirationModal, showTutorialHighlight]);

  // Tutorial effect: Show training-intro on turn 1
  useEffect(() => {
    if (careerData && careerData.turn === 1 && isTutorialStep('training-intro') && !hasShownStep('training-intro')) {
      setShowTutorialOverlay('training-intro');
      markStepShown('training-intro');
    }
  }, [careerData?.turn, isTutorialStep, hasShownStep, markStepShown]);

  // Tutorial effect: Show battle-intro after first training action (turn 2+)
  // Only show when in training view with no active events
  useEffect(() => {
    if (careerData &&
        careerData.turn >= 2 &&
        viewMode === 'training' &&
        !careerData.eventResult &&
        !careerData.pendingEvent &&
        isTutorialStep('battle-intro') &&
        !hasShownStep('battle-intro')) {
      setShowTutorialOverlay('battle-intro');
      markStepShown('battle-intro');
    }
  }, [careerData?.turn, careerData?.eventResult, careerData?.pendingEvent, viewMode, isTutorialStep, hasShownStep, markStepShown]);

  // Tutorial effect: Show mood-intro after battle tutorial (turn 3+)
  // Only show when in training view with no active events
  useEffect(() => {
    if (careerData &&
        careerData.turn >= 3 &&
        viewMode === 'training' &&
        !careerData.eventResult &&
        !careerData.pendingEvent &&
        isTutorialStep('mood-intro') &&
        !hasShownStep('mood-intro')) {
      setShowTutorialOverlay('mood-intro');
      markStepShown('mood-intro');
    }
  }, [careerData?.turn, careerData?.eventResult, careerData?.pendingEvent, viewMode, isTutorialStep, hasShownStep, markStepShown]);

  // Helper function to check if player can afford any learnable move
  const getAffordableMoves = () => {
    if (!careerData || !careerData.pokemon) return [];

    const baseLearnables = careerData.pokemon.learnableAbilities || [];
    const hintedMoves = Object.keys(careerData.moveHints || {});
    const knownMoves = careerData.knownAbilities || [];
    const skillPoints = careerData.skillPoints ?? 0;

    // Combine base learnable moves with hinted moves (excluding already known)
    const additionalFromHints = hintedMoves.filter(
      move => !baseLearnables.includes(move) && !knownMoves.includes(move) && MOVES[move]
    );
    const allLearnables = [...baseLearnables, ...additionalFromHints];

    // Filter to affordable moves that aren't already known
    return allLearnables.filter(moveName => {
      if (!MOVES[moveName] || knownMoves.includes(moveName)) return false;

      const move = MOVES[moveName];
      // moveHints stores the accumulated discount value directly (e.g., 0.15, 0.30, etc.)
      const discount = (careerData.moveHints || {})[moveName] || 0;
      const finalCost = Math.ceil(move.cost * (1 - discount));

      return skillPoints >= finalCost;
    });
  };

  // Tutorial effect: Show move-intro when user can afford to learn a move
  // Only show when in training view with no active events
  useEffect(() => {
    if (careerData &&
        viewMode === 'training' &&
        !careerData.eventResult &&
        !careerData.pendingEvent &&
        isTutorialStep('move-intro') &&
        !hasShownStep('move-intro')) {
      const affordableMoves = getAffordableMoves();
      if (affordableMoves.length > 0) {
        setShowTutorialOverlay('move-intro');
        markStepShown('move-intro');
      }
    }
  }, [careerData?.skillPoints, careerData?.pokemon, careerData?.moveHints, careerData?.knownAbilities, careerData?.eventResult, careerData?.pendingEvent, viewMode, isTutorialStep, hasShownStep, markStepShown]);

  // Tutorial effect: Show move-learn when user switches to learn view
  useEffect(() => {
    if (viewMode === 'learn' &&
        isTutorialStep('move-learn') &&
        !hasShownStep('move-learn')) {
      // Clear highlight first, then show modal
      setShowTutorialHighlight(null);
      setShowTutorialOverlay('move-learn');
      markStepShown('move-learn');
    }
  }, [viewMode, isTutorialStep, hasShownStep, markStepShown]);

  // Tutorial effect: Show rest-intro when energy drops below 50
  // Only show when in training view with no active events
  useEffect(() => {
    if (careerData &&
        careerData.energy < 50 &&
        viewMode === 'training' &&
        !careerData.eventResult &&
        !careerData.pendingEvent &&
        isTutorialStep('rest-intro') &&
        !hasShownStep('rest-intro')) {
      setShowTutorialOverlay('rest-intro');
      markStepShown('rest-intro');
    }
  }, [careerData?.energy, careerData?.eventResult, careerData?.pendingEvent, viewMode, isTutorialStep, hasShownStep, markStepShown]);

  // Tutorial effect: Show gym-intro at turn 12 (first gym)
  useEffect(() => {
    if (careerData && careerData.turn === 12 && isTutorialStep('gym-intro') && !hasShownStep('gym-intro')) {
      setShowTutorialOverlay('gym-intro');
      markStepShown('gym-intro');
    }
  }, [careerData?.turn, isTutorialStep, hasShownStep, markStepShown]);

  // Effect: Check for evolution when returning from a battle that the player won
  // Battles apply stat gains on the server BEFORE animation plays,
  // so we check for evolution when transitioning from battleState to null
  //
  // IMPORTANT: We track pendingEvolutionCheck separately because:
  // 1. When battleState becomes null, careerData might still have OLD stats (React state is async)
  // 2. We need to wait for careerData to update with the new stats before checking evolution
  // 3. The ref approach alone doesn't work because we update the ref immediately
  useEffect(() => {
    const wasBattling = prevBattleStateRef.current !== null;
    const nowNotBattling = battleState === null;
    const playerWon = prevBattleStateRef.current?.winner === 'player';

    console.log('[Battle->Evolution] useEffect triggered:', {
      wasBattling,
      nowNotBattling,
      playerWon,
      prevWinner: prevBattleStateRef.current?.winner,
      hasCareerData: !!careerData,
      pokemonName: careerData?.pokemon?.name,
      evolutionStage: careerData?.evolutionStage,
      currentStats: careerData?.currentStats,
      pendingEvolutionCheck: pendingEvolutionCheckRef.current
    });

    // When transitioning from battle to no-battle after a win, mark that we need to check evolution
    if (wasBattling && nowNotBattling && playerWon) {
      console.log('[Battle->Evolution] Battle ended with victory, marking pending evolution check');
      pendingEvolutionCheckRef.current = true;
    }

    // If we have a pending evolution check AND careerData is available, check now
    // This handles the case where careerData updates AFTER battleState becomes null
    if (pendingEvolutionCheckRef.current && careerData && nowNotBattling) {
      console.log('[Battle->Evolution] Pending check + careerData available, checking for evolution...');
      checkAndTriggerEvolution(careerData);
      pendingEvolutionCheckRef.current = false;
    }

    // Update the ref for next render
    prevBattleStateRef.current = battleState;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [battleState, careerData]);

  useEffect(() => {
    // Skip if still processing an event resolution (prevents flicker during state transition)
    if (isProcessingEvent) return;

    // Skip if a training/rest action is in progress (prevents race condition where
    // triggerEvent/generateTraining overwrites training options while user's action is pending)
    if (isProcessingAction) return;

    // Skip if we just declined an event and are waiting for training options
    if (declinedEventRef.current) return;

    if (careerData && !careerData.currentTrainingOptions && !careerData.pendingEvent && !careerData.eventResult && !evolutionModal && !inspirationModal) {
      // Don't generate training on gym turns or Elite 4 turns
      const nextGymTurn = (careerData.currentGymIndex + 1) * GAME_CONFIG.CAREER.GYM_LEADER_INTERVAL;
      const isGymTurn = careerData.turn === nextGymTurn && careerData.currentGymIndex < 4;
      const eliteFourStartTurn = GAME_CONFIG.CAREER.ELITE_FOUR_START_TURN || 60;
      const eliteFourIdx = careerData.turn - eliteFourStartTurn;
      const isEliteFourTurn = careerData.turn >= eliteFourStartTurn && eliteFourIdx >= 0 && eliteFourIdx < 4 && !careerData.eliteFourDefeated?.[eliteFourIdx];
      if (isGymTurn || isEliteFourTurn) return;

      // Check if we've already processed this turn
      const justExitedBattle = !battleState && lastProcessedTurnRef.current === careerData.turn;
      if (lastProcessedTurnRef.current === careerData.turn && !justExitedBattle) return;

      // Mark this turn as processed
      lastProcessedTurnRef.current = careerData.turn;

      // Inspiration turns are reserved - no random events
      const inspirationTurns = [11, 23, 35, 47, 59];
      const isInspirationTurn = inspirationTurns.includes(careerData.turn);

      // SERVER-AUTHORITATIVE: Let server decide if event should trigger
      const shouldCheckForEvent = careerData.turn > 1 && careerData.turn !== nextGymTurn && !justExitedBattle && !isInspirationTurn;

      if (shouldCheckForEvent) {
        // Server will decide if an event occurs (50% chance) or generate training options
        triggerEvent();
      } else {
        // No event possible, generate training options from server
        generateTraining();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [careerData?.turn, careerData?.currentTrainingOptions, careerData?.pendingEvent, careerData?.eventResult, evolutionModal, inspirationModal, battleState, isProcessingEvent, isProcessingAction]);

  // Null check AFTER all hooks
  if (!careerData) {
    return null;
  }

  // ============================================================================
  // MODAL COMPONENTS
  // ============================================================================

  const EvolutionModal = () => {
    console.log('EvolutionModal render - evolutionModal:', evolutionModal);
    if (!evolutionModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-2xl p-6 max-w-md w-full shadow-card-lg text-center"
        >
          <h2 className="text-2xl font-bold text-pocket-red mb-4">Evolution!</h2>

          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="text-center">
              <div className="mb-2">
                {generatePokemonSprite(POKEMON[evolutionModal.fromName]?.primaryType || 'Normal', evolutionModal.fromName, 64, false)}
              </div>
              <p className="font-bold text-pocket-text">{evolutionModal.fromName}</p>
              <span
                className="px-2 py-0.5 rounded-full text-xs font-bold text-white"
                style={{ backgroundColor: getGradeColor(getPokemonGrade(evolutionModal.oldStats)) }}
              >
                {getPokemonGrade(evolutionModal.oldStats)}
              </span>
            </div>

            <div className="text-3xl text-amber-400 animate-pulse">→</div>

            <div className="text-center">
              <div className="mb-2">
                {generatePokemonSprite(POKEMON[evolutionModal.toName]?.primaryType || 'Normal', evolutionModal.toName, 64, false)}
              </div>
              <p className="font-bold text-pocket-text">{evolutionModal.toName}</p>
              <span
                className="px-2 py-0.5 rounded-full text-xs font-bold text-white"
                style={{ backgroundColor: getGradeColor(getPokemonGrade(evolutionModal.newStats)) }}
              >
                {getPokemonGrade(evolutionModal.newStats)}
              </span>
            </div>
          </div>

          <div className="bg-pocket-bg rounded-xl p-4 mb-4">
            <p className="font-bold text-pocket-text mb-2 text-sm">Stats increased by 10%!</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {Object.keys(evolutionModal.newStats).map(stat => {
                const oldVal = evolutionModal.oldStats[stat];
                const newVal = evolutionModal.newStats[stat];
                return (
                  <div key={stat} className="flex justify-between text-pocket-text-light">
                    <span>{stat}:</span>
                    <span className="text-pocket-green font-bold">{oldVal} → {newVal}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <button
            onClick={() => applyEvolution(evolutionModal.fromName, evolutionModal.toName, evolutionModal.toStage)}
            className="pocket-btn-primary w-full py-3 text-lg"
          >
            Evolve!
          </button>
        </motion.div>
      </div>
    );
  };

  // Strategy Selector Modal - allows player to choose battle strategy
  const StrategySelector = () => {
    if (!showStrategySelector || !careerData?.pokemon?.strategyAptitudes) return null;

    const strategies = ['Scaler', 'Nuker', 'Debuffer', 'Chipper', 'MadLad'];
    const strategyDescriptions = {
      Scaler: 'Buffs first, then highest damage moves',
      Nuker: 'Saves stamina for powerful attacks',
      Debuffer: 'Prioritizes status effects before damage',
      Chipper: 'Rapid low-stamina attacks for pressure',
      MadLad: 'Completely random move selection'
    };

    const handleStrategySelect = async (strategy) => {
      const result = await changeStrategy(strategy);
      if (result) {
        setShowStrategySelector(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowStrategySelector(false)}>
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-2xl p-4 max-w-sm w-full shadow-card-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="text-lg font-bold text-pocket-text mb-3 text-center">Select Battle Strategy</h2>

          <div className="space-y-2">
            {strategies.map(strategy => {
              const grade = careerData.pokemon.strategyAptitudes[strategy] || 'C';
              const isSelected = careerData.pokemon.strategy === strategy;

              return (
                <button
                  key={strategy}
                  onClick={() => handleStrategySelect(strategy)}
                  className={`w-full p-3 rounded-xl border-2 transition-all text-left ${
                    isSelected
                      ? 'border-pocket-blue bg-blue-50'
                      : 'border-gray-200 hover:border-gray-400 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-pocket-text">{strategy}</span>
                      {isSelected && (
                        <span className="text-xs text-pocket-blue font-bold">(Active)</span>
                      )}
                    </div>
                    <span
                      className="px-2 py-0.5 rounded-full text-xs font-bold text-white"
                      style={{ backgroundColor: getAptitudeColor(grade) }}
                    >
                      {grade}
                    </span>
                  </div>
                  <p className="text-xs text-pocket-text-light mt-1">{strategyDescriptions[strategy]}</p>
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setShowStrategySelector(false)}
            className="w-full mt-4 py-2 rounded-xl border-2 border-gray-300 text-pocket-text font-semibold hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
        </motion.div>
      </div>
    );
  };

  const InspirationModal = () => {
    console.log('[InspirationModal] Render, modal state:', inspirationModal);
    if (!inspirationModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-2xl p-6 max-w-2xl w-full shadow-card-lg"
        >
          <h2 className="text-2xl font-bold text-amber-500 mb-2 text-center">Inspiration!</h2>
          <p className="text-pocket-text-light text-center mb-4 text-sm">Your trained Pokemon inspire you at Turn {inspirationModal.turn}!</p>

          <div className="space-y-3">
            {inspirationModal.results.map((result, idx) => (
              <div key={idx} className="bg-pocket-bg rounded-xl p-4">
                <h3 className="text-lg font-bold text-pocket-text mb-2">{result.pokemonName}</h3>

                {result.statBonus && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-2">
                    <div className="flex justify-between items-center">
                      <span className="text-pocket-green font-bold">
                        {result.statBonus.stat} +{result.statBonus.amount}
                      </span>
                      <div className="flex gap-0.5">
                        {[...Array(result.statBonus.stars)].map((_, i) => (
                          <Star key={i} size={14} className="text-amber-400 fill-amber-400" />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {result.aptitudeUpgrade && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-2">
                    <div className="text-purple-700 font-bold mb-1 text-sm">
                      {result.aptitudeUpgrade.type} Aptitude: {result.aptitudeUpgrade.from} → {result.aptitudeUpgrade.to}
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-pocket-text-light">
                        {(result.aptitudeUpgrade.chance * 100).toFixed(0)}% chance
                      </span>
                      <div className="flex gap-0.5">
                        {[...Array(result.aptitudeUpgrade.stars)].map((_, i) => (
                          <Star key={i} size={14} className="text-amber-400 fill-amber-400" />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {result.strategyUpgrade && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <div className="text-orange-700 font-bold mb-1 text-sm">
                      {result.strategyUpgrade.strategy} Strategy: {result.strategyUpgrade.from} → {result.strategyUpgrade.to}
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-pocket-text-light">
                        {(result.strategyUpgrade.chance * 100).toFixed(0)}% chance
                      </span>
                      <div className="flex gap-0.5">
                        {[...Array(result.strategyUpgrade.stars)].map((_, i) => (
                          <Star key={i} size={14} className="text-amber-400 fill-amber-400" />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {!result.statBonus && !result.aptitudeUpgrade && !result.strategyUpgrade && (
                  <div className="text-pocket-text-light text-sm italic">
                    No bonuses this time
                  </div>
                )}
              </div>
            ))}
          </div>

          <button
            onClick={() => {
              // Server has already applied the inspiration bonuses via applyInspirationOnServer()
              // Just close the modal - career state was updated when server responded
              console.log('[InspirationModal] Closing modal - bonuses already applied by server');
              setInspirationModal(null);
              processNextModal(); // Process next modal in queue
              // Check for evolution after inspiration bonuses may have pushed stats to evolution threshold
              checkAndTriggerEvolution(careerData);
            }}
            className="pocket-btn-primary w-full mt-4 py-3 text-lg"
          >
            Continue
          </button>
        </motion.div>
      </div>
    );
  };

  const PokeclockModal = () => {
    if (!pokeclockModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-2xl p-8 max-w-md w-full shadow-card-lg text-center"
        >
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-pocket-blue/10 flex items-center justify-center">
            <Clock size={40} className="text-pocket-blue animate-pulse" />
          </div>
          <h2 className="text-2xl font-bold text-pocket-blue mb-2">Pokeclock Used!</h2>
          <p className="text-pocket-text-light">You get another chance!</p>
        </motion.div>
      </div>
    );
  };

  const HelpModal = () => {
    if (!showHelp) return null;

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-end p-4"
        onClick={() => setShowHelp(false)}
        style={{ overflow: 'hidden' }}
      >
        <motion.div
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          className="bg-white rounded-2xl w-full sm:w-[500px] shadow-card-lg"
          style={{
            height: 'calc(100vh - 2rem)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-white border-b border-pocket-bg p-4 flex justify-between items-center rounded-t-2xl" style={{ flexShrink: 0 }}>
            <div className="flex items-center gap-2">
              <HelpCircle size={20} className="text-pocket-blue" />
              <h2 className="text-lg font-bold text-pocket-text">Game Guide</h2>
            </div>
            <button onClick={() => setShowHelp(false)} className="p-2 text-pocket-text-light hover:text-pocket-text hover:bg-pocket-bg rounded-lg transition-colors">{ICONS.CLOSE}</button>
          </div>

          <div
            className="p-4 space-y-4"
            style={{
              flex: 1,
              overflowY: 'scroll',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            <section>
              <h3 className="text-base font-bold text-pocket-red mb-2 border-b border-pocket-bg pb-2">Pokemon</h3>
              <div className="space-y-3">
                <div>
                  <h4 className="font-bold text-gray-800 mb-1">Stats</h4>
                  <div className="text-sm space-y-1 ml-2">
                    <p><strong>HP:</strong> Health points in battle</p>
                    <p><strong>Attack:</strong> Damage output</p>
                    <p><strong>Defense:</strong> Damage mitigation</p>
                    <p><strong>Instinct:</strong> Crit chance & dodge chance</p>
                    <p><strong>Speed:</strong> Stamina regeneration rate</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-1">Type Aptitudes</h4>
                  <p className="text-sm ml-2">Grades (F-S) multiply damage when using that type.</p>
                  <div className="text-sm ml-2 mt-1 grid grid-cols-2 gap-1">
                    <span><strong style={{color: '#000'}}>F:</strong> 60%</span>
                    <span><strong style={{color: '#9ca3af'}}>E:</strong> 70%</span>
                    <span><strong style={{color: '#3b82f6'}}>D:</strong> 80%</span>
                    <span><strong style={{color: '#22c55e'}}>C:</strong> 90%</span>
                    <span><strong style={{color: '#ec4899'}}>B:</strong> 100%</span>
                    <span><strong style={{color: '#f97316'}}>A:</strong> 110%</span>
                    <span><strong style={{color: '#eab308'}}>S:</strong> 120%</span>
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-1">Type Matchups</h4>
                  <p className="text-sm ml-2">Super effective attacks deal +20% damage.</p>
                  <div className="text-sm ml-2 space-y-1 mt-1">
                    <p>Fire {ICONS.ARROW_RIGHT} Grass {ICONS.ARROW_RIGHT} Water {ICONS.ARROW_RIGHT} Fire</p>
                    <p>Fighting {ICONS.ARROW_RIGHT} Electric</p>
                    <p>Psychic {ICONS.ARROW_RIGHT} Fighting</p>
                    <p>Psychic {ICONS.ARROW_DOUBLE} Psychic (neutral)</p>
                    <p>Fighting {ICONS.ARROW_RIGHT} Normal</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-1">Strategies</h4>
                  <p className="text-sm ml-2">Each Pokemon has a preferred battle strategy:</p>
                  <div className="text-sm ml-2 space-y-1">
                    <p><strong>Scaler:</strong> Buffs first, then highest damage moves</p>
                    <p><strong>Nuker:</strong> Saves stamina for powerful attacks</p>
                    <p><strong>Debuffer:</strong> Prioritizes status effects</p>
                    <p><strong>Chipper:</strong> Rapid low-stamina attacks</p>
                    <p><strong>MadLad:</strong> Random move selection</p>
                  </div>
                  <p className="text-sm ml-2 mt-1">Strategy aptitude grade affects stamina costs.</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-base font-bold text-pocket-red mb-2 border-b border-pocket-bg pb-2">Battles</h3>
              <div className="space-y-3">
                <div>
                  <h4 className="font-bold text-gray-800 mb-1">Combat</h4>
                  <p className="text-sm ml-2">Real-time auto-battle system. Pokemon automatically use moves based on stamina and cooldowns.</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-1">Stamina</h4>
                  <p className="text-sm ml-2">Required to use moves. Regenerates when resting (not using moves). Speed stat increases regen rate.</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-1">Moves</h4>
                  <div className="text-sm ml-2 space-y-1">
                    <p><strong>Warmup:</strong> Ticks before move becomes available</p>
                    <p><strong>Cooldown:</strong> Ticks before move can be used again</p>
                    <p><strong>Stamina Cost:</strong> Modified by strategy grade (better grade = lower cost)</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-1">Accuracy & Critical Hits</h4>
                  <div className="text-sm ml-2 space-y-1">
                    <p>Resting grants dodge chance (Instinct/2786)</p>
                    <p>Crit chance: 5% + (Instinct/800) for 2{ICONS.MULTIPLY} damage</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-1">Status Effects</h4>
                  <div className="text-sm ml-2 space-y-1">
                    <p><strong>Burn/Poison:</strong> Damage over time</p>
                    <p><strong>Paralyze:</strong> -25% accuracy</p>
                    <p><strong>Stun:</strong> Cannot act</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-1">Damage Formula</h4>
                  <p className="text-sm ml-2">Base {ICONS.MULTIPLY} (Attack/Defense) {ICONS.MULTIPLY} Type Aptitude {ICONS.MULTIPLY} Type Matchup {ICONS.MULTIPLY} Crit Multiplier</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-base font-bold text-pocket-red mb-2 border-b border-pocket-bg pb-2">Career</h3>
              <div className="space-y-3">
                <div>
                  <h4 className="font-bold text-gray-800 mb-1">Goal</h4>
                  <p className="text-sm ml-2">Defeat 5 Gym Leaders within 60 turns. Gym battles occur at turns 12, 24, 36, 48, and 60.</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-1">Energy</h4>
                  <p className="text-sm ml-2">Required for training and battles. At 0 energy, training has 99% fail rate. Resting restores 30-70 energy.</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-1">Training</h4>
                  <div className="text-sm ml-2 space-y-1">
                    <p><strong>Success:</strong> Gain stats, 3 Skill Points, 7 friendship</p>
                    <p><strong>Failure:</strong> Lose 2 stats</p>
                    <p><strong>Support Bonus:</strong> 100 friendship grants major stat boost</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-1">Wild Battles</h4>
                  <p className="text-sm ml-2">Win: +5 all stats, +20 SP. Cost: -25 energy. At 0 energy: 50% chance to lose 10 all stats.</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-1">Gym Battles</h4>
                  <p className="text-sm ml-2">Must win or Game Over. Preview upcoming gym leader stats and moves. Each gym progressively harder.</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-1">Learning Moves</h4>
                  <div className="text-sm ml-2 space-y-1">
                    <p>Spend Skill Points to learn new moves</p>
                    <p>Base cost {ICONS.MULTIPLY} 3.0, reduced by move hints (up to -60%)</p>
                    <p>Hints earned through support hangouts</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-1">Events</h4>
                  <p className="text-sm ml-2">50% chance per turn. Includes stat gains, choices, wild battles, setbacks, and support hangouts (at 80+ friendship).</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-base font-bold text-pocket-red mb-2 border-b border-pocket-bg pb-2">Gacha</h3>
              <div className="space-y-3">
                <div>
                  <h4 className="font-bold text-gray-800 mb-1">Pokemon Gachapon</h4>
                  <p className="text-sm ml-2">Draw random Pokemon to add to your collection. Each Pokemon has unique base stats, type aptitudes, strategies, and learnable moves.</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-1">Rarity Tiers</h4>
                  <p className="text-sm ml-2">Pokemon are graded by total base stats (D to S+). Higher grades are more powerful but rarer.</p>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-1">Starting Fresh</h4>
                  <p className="text-sm ml-2">Each career run starts with a new Pokemon at base stats. Use gacha to find Pokemon suited to your strategy.</p>
                </div>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    );
  };

  // ============================================================================
  // RENDER MAIN SCREEN
  // ============================================================================

  // Show gym battle when turn matches the next gym's designated turn (turns 12, 24, 36, 48 = 4 gyms)
  const nextGymTurn = (careerData.currentGymIndex + 1) * GAME_CONFIG.CAREER.GYM_LEADER_INTERVAL;
  const isGymTurn = careerData.turn === nextGymTurn && careerData.currentGymIndex < 4;
  const nextGymLeader = careerData.gymLeaders?.[careerData.currentGymIndex];

  console.log('[CareerScreen] Render - Turn:', careerData.turn, 'GymIndex:', careerData.currentGymIndex, 'NextGymTurn:', nextGymTurn, 'IsGymTurn:', isGymTurn);

  // Elite 4 battles on turns 60, 61, 62, 63
  const eliteFourStartTurn = GAME_CONFIG.CAREER.ELITE_FOUR_START_TURN || 60;
  const eliteFourIndex = careerData.turn - eliteFourStartTurn;
  const isEliteFourTurn = careerData.turn >= eliteFourStartTurn && eliteFourIndex >= 0 && eliteFourIndex < 4 && !careerData.eliteFourDefeated?.[eliteFourIndex];
  const currentEliteFour = isEliteFourTurn && ELITE_FOUR ? ELITE_FOUR[eliteFourIndex] : null;

  // Combined battle turn check
  const isBattleTurn = isGymTurn || isEliteFourTurn;

  return (
    <>
      <div className="w-full min-h-screen bg-pocket-bg p-2 sm:p-4">
        <div className="max-w-7xl mx-auto space-y-3">
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="rounded-2xl overflow-hidden shadow-lg"
          >
            {/* Pokeball Red Header Strip */}
            <div className="bg-gradient-to-r from-pokeball-red via-pokeball-red-light to-pokeball-red h-2" />

            {/* Main Card Content */}
            <div className="pokeball-card rounded-t-none p-3 sm:p-4">
              {/* Mobile: Stack everything vertically, Desktop: Side by side */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                {/* Pokemon Info */}
                <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                  {/* Pokemon Sprite */}
                  <div className="flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24">
                    {generatePokemonSprite(careerData.pokemon.primaryType, careerData.pokemon.name, 96, false)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h2 className="text-base sm:text-xl font-bold truncate text-pokeball-black">{careerData.pokemon.name}</h2>
                      <span
                        className="px-2 py-0.5 rounded-lg text-xs font-bold text-white flex-shrink-0 shadow-sm"
                        style={{ backgroundColor: getGradeColor(getPokemonGrade(careerData.currentStats)) }}
                      >
                        {getPokemonGrade(careerData.currentStats)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-pokeball-black-light">
                      <TypeBadge type={careerData.pokemon.primaryType} size={14} />
                      {careerData.pokemon.strategyAptitudes && (
                        <button
                          onClick={() => setShowStrategySelector(true)}
                          className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-pokeball-black text-white hover:bg-pokeball-black-light transition-colors cursor-pointer shadow-sm"
                          title="Click to change strategy"
                        >
                          {careerData.pokemon.strategy || 'Select'} <span style={{ color: getAptitudeColor(careerData.pokemon.strategyGrade || 'C') }}>({careerData.pokemon.strategyGrade || '?'})</span>
                        </button>
                      )}
                    </div>
                    {/* Type Aptitudes - Sorted by grade */}
                    {careerData.pokemon.typeAptitudes && (
                    <div className="flex text-[10px] sm:text-xs mt-1 flex-wrap gap-x-2 gap-y-0.5">
                      {sortAptitudesByGrade(careerData.pokemon.typeAptitudes).map(([type, grade]) => (
                        <motion.span
                          key={type}
                          layout
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                          className="inline-flex items-center gap-0.5"
                        >
                          <TypeIcon type={type} size={12} />
                          <span style={{ color: getAptitudeColor(grade), fontWeight: 'bold' }}>{grade || '?'}</span>
                        </motion.span>
                      ))}
                    </div>
                    )}
                    {/* Stat Preferences */}
                    {careerData.pokemon.statPreferences && (
                      <div className="flex text-[10px] sm:text-xs mt-1 gap-2">
                        <span className="text-pokeball-black-light">
                          Training: <span className="font-bold text-amber-600">{careerData.pokemon.statPreferences.preferred} +20%</span>, <span className="font-bold text-amber-500">{careerData.pokemon.statPreferences.secondary} +10%</span>
                        </span>
                      </div>
                    )}
                    {/* Stats Grid - Now with dark accents */}
                    <div className="grid grid-cols-5 gap-x-1 sm:gap-x-2 text-xs mt-2">
                      <div className="flex items-center gap-0.5 sm:gap-1 bg-pokeball-black/5 rounded px-1 py-0.5">
                        <StatIcon stat="HP" size={10} />
                        <span className="font-bold text-[10px] sm:text-xs text-pokeball-black">{careerData.currentStats.HP}</span>
                      </div>
                      <div className="flex items-center gap-0.5 sm:gap-1 bg-pokeball-black/5 rounded px-1 py-0.5">
                        <StatIcon stat="Attack" size={10} />
                        <span className="font-bold text-[10px] sm:text-xs text-pokeball-black">{careerData.currentStats.Attack}</span>
                      </div>
                      <div className="flex items-center gap-0.5 sm:gap-1 bg-pokeball-black/5 rounded px-1 py-0.5">
                        <StatIcon stat="Defense" size={10} />
                        <span className="font-bold text-[10px] sm:text-xs text-pokeball-black">{careerData.currentStats.Defense}</span>
                      </div>
                      <div className="flex items-center gap-0.5 sm:gap-1 bg-pokeball-black/5 rounded px-1 py-0.5">
                        <StatIcon stat="Instinct" size={10} />
                        <span className="font-bold text-[10px] sm:text-xs text-pokeball-black">{careerData.currentStats.Instinct}</span>
                      </div>
                      <div className="flex items-center gap-0.5 sm:gap-1 bg-pokeball-black/5 rounded px-1 py-0.5">
                        <StatIcon stat="Speed" size={10} />
                        <span className="font-bold text-[10px] sm:text-xs text-pokeball-black">{careerData.currentStats.Speed}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Turn Counter and Action Buttons */}
                <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3">
                  <div className="text-left sm:text-right flex-shrink-0 bg-pokeball-red/10 rounded-lg px-3 py-1.5">
                    <div className="font-bold text-sm sm:text-base text-pokeball-red">Turn {careerData.turn}/{GAME_CONFIG.CAREER.TOTAL_TURNS}</div>
                    <div className="text-xs text-pokeball-black-light">
                      {careerData.turn < eliteFourStartTurn
                        ? `Next Gym: Turn ${careerData.currentGymIndex < 4 ? (careerData.currentGymIndex + 1) * 12 : eliteFourStartTurn}`
                        : `Elite 4: ${(careerData.eliteFourDefeated?.filter(Boolean)?.length || 0)}/4`
                      }
                    </div>
                  </div>
                  <div className="flex gap-1 sm:gap-2 flex-wrap">
                    <button
                      onClick={() => setViewMode('abilities')}
                      className="flex items-center gap-0.5 sm:gap-1 px-2 sm:px-3 py-1.5 sm:py-2 bg-white border-2 border-pokeball-black/20 hover:border-pokeball-red hover:bg-pokeball-red/5 rounded-lg transition cursor-pointer text-xs sm:text-sm shadow-sm"
                    >
                      <Sparkles size={12} className="sm:w-3.5 sm:h-3.5 text-purple-600" />
                      <span className="font-bold text-pokeball-black">{careerData.knownAbilities.length}</span>
                    </button>
                    <button
                      onClick={() => setViewMode('learn')}
                      className="tutorial-sp-book flex items-center gap-0.5 sm:gap-1 px-2 sm:px-3 py-1.5 sm:py-2 bg-white border-2 border-pokeball-black/20 hover:border-pokeball-red hover:bg-pokeball-red/5 rounded-lg transition cursor-pointer text-xs sm:text-sm shadow-sm"
                    >
                      <Book size={12} className="sm:w-3.5 sm:h-3.5 text-blue-600" />
                      <span className="font-bold text-pokeball-black">{careerData.skillPoints ?? 0}</span>
                    </button>
                    <button
                      onClick={() => setViewMode('gym')}
                      className="flex items-center gap-0.5 sm:gap-1 px-2 sm:px-3 py-1.5 sm:py-2 bg-white border-2 border-pokeball-black/20 hover:border-pokeball-red hover:bg-pokeball-red/5 rounded-lg transition cursor-pointer text-xs sm:text-sm shadow-sm"
                    >
                      <Trophy size={12} className="sm:w-3.5 sm:h-3.5 text-yellow-600" />
                      <span className="font-bold text-pokeball-black">{careerData.currentGymIndex}/4</span>
                    </button>
                    <div className="flex items-center gap-0.5 sm:gap-1 px-2 sm:px-3 py-1.5 sm:py-2 bg-white border-2 border-pokeball-black/20 rounded-lg text-xs sm:text-sm shadow-sm" title="Pokeclocks: Retry gym battles">
                      <Clock size={12} className="sm:w-3.5 sm:h-3.5 text-pink-600" />
                      <span className="font-bold text-pokeball-black">{careerData.pokeclocks || 0}</span>
                    </div>
                    <button
                      onClick={() => setGameState('menu')}
                      className="flex items-center gap-0.5 sm:gap-1 px-2 sm:px-3 py-1.5 sm:py-2 bg-white border-2 border-pokeball-black/20 hover:border-pokeball-black hover:bg-pokeball-black/5 rounded-lg transition cursor-pointer text-xs sm:text-sm shadow-sm"
                      title="Return to Homescreen"
                    >
                      <Home size={12} className="sm:w-3.5 sm:h-3.5 text-pokeball-black" />
                    </button>
                    <button
                      onClick={() => setShowAbandonConfirm(true)}
                      className="flex items-center gap-0.5 sm:gap-1 px-2 sm:px-3 py-1.5 sm:py-2 bg-pokeball-red/10 border-2 border-pokeball-red/30 hover:bg-pokeball-red hover:text-white hover:border-pokeball-red-dark rounded-lg transition cursor-pointer text-xs sm:text-sm shadow-sm"
                      title="Abandon Career"
                    >
                      <LogOut size={12} className="sm:w-3.5 sm:h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {/* Pokeball Black Bottom Strip */}
            <div className="bg-pokeball-black h-1" />
          </motion.div>

          {isGymTurn && nextGymLeader && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-pocket-red text-white rounded-2xl p-4 shadow-card"
            >
              <div className="flex items-center justify-between flex-col sm:flex-row gap-3">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg border-2 border-yellow-400 overflow-hidden bg-gray-800 flex-shrink-0">
                    <img
                      src={getGymLeaderImage(nextGymLeader.name)}
                      alt={nextGymLeader.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">GYM LEADER BATTLE!</h3>
                    <p className="text-sm">Face {nextGymLeader.name} now!</p>
                  </div>
                </div>
                <button
                  onClick={() => startBattle(nextGymLeader, true)}
                  className="bg-white text-red-600 px-6 py-2 rounded-lg font-bold hover:bg-gray-100 transition"
                >
                  Challenge
                </button>
              </div>
            </motion.div>
          )}

          {isEliteFourTurn && currentEliteFour && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-2xl p-4 shadow-card"
            >
              <div className="flex items-center justify-between flex-col sm:flex-row gap-4">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg border-2 border-yellow-400 overflow-hidden bg-gray-800 flex-shrink-0">
                    <img
                      src={getGymLeaderImage(currentEliteFour.name)}
                      alt={currentEliteFour.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-purple-300 font-semibold">Elite Four Member {eliteFourIndex + 1}/4</div>
                    <h3 className="text-xl sm:text-2xl font-bold">{currentEliteFour.name}</h3>
                    <p className="text-sm text-purple-200">{currentEliteFour.title}</p>
                    <p className="text-xs mt-1">Ace: {currentEliteFour.pokemon.name} ({currentEliteFour.type})</p>
                  </div>
                </div>
                <div className="text-center sm:text-right">
                  <button
                    onClick={() => {
                      // Pass Elite 4 opponent data - server will scale stats and derive strategy from strategyAptitudes
                      const pokemon = currentEliteFour.pokemon;
                      const eliteOpponent = {
                        name: currentEliteFour.name,
                        pokemon: pokemon,
                        primaryType: pokemon.primaryType,
                        baseStats: pokemon.baseStats,
                        abilities: [...(pokemon.defaultAbilities || []), ...(pokemon.learnableAbilities || [])],
                        typeAptitudes: pokemon.typeAptitudes,
                        strategyAptitudes: pokemon.strategyAptitudes
                      };
                      startBattle(eliteOpponent, true, true); // isGymLeader=true, isEliteFour=true
                    }}
                    className="bg-yellow-400 text-purple-900 px-6 py-3 rounded-lg font-bold hover:bg-yellow-300 transition text-lg"
                  >
                    Challenge Elite Four!
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* PENDING EVENT SCREEN */}
          {!isBattleTurn && careerData.pendingEvent && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`rounded-2xl p-4 shadow-card ${
                careerData.pendingEvent.type === 'hangout'
                  ? 'rainbow-sheen text-center'
                  : 'bg-white border-l-4 border-amber-500'
              }`}
            >
              <h3 className="text-lg font-bold mb-2 text-pocket-text">{careerData.pendingEvent.name}</h3>
              <p className="text-pocket-text-light mb-4 text-sm">{careerData.pendingEvent.description}</p>

              {careerData.pendingEvent.type === 'stat_increase' && (
                  <div className="space-y-3">
                    {/* Stat Gains */}
                    {(() => {
                      const statNames = ['HP', 'Attack', 'Defense', 'Instinct', 'Speed'];
                      const statEffects = Object.entries(careerData.pendingEvent.effect).filter(([key]) => statNames.includes(key));
                      return statEffects.length > 0 ? (
                        <div className="bg-green-50 p-3 rounded border-2 border-green-500">
                          <div className="font-bold text-green-700 mb-2 text-center">Stat Gains:</div>
                          <div className="flex justify-center">
                            <div className="grid grid-cols-5 gap-2 text-sm max-w-md">
                              {statEffects.map(([stat, value]) => (
                                <div key={stat} className="text-center">
                                  <div className="text-gray-600">{stat}</div>
                                  <div className="font-bold text-green-600">+{value}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : null;
                    })()}
                    {/* Energy */}
                    {careerData.pendingEvent.effect.energy && (
                      <div className={`p-3 rounded border-2 ${careerData.pendingEvent.effect.energy > 0 ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'}`}>
                        <div className="font-bold text-center">
                          Energy: <span className={careerData.pendingEvent.effect.energy > 0 ? 'text-green-600' : 'text-red-600'}>
                            {careerData.pendingEvent.effect.energy > 0 ? '+' : ''}{careerData.pendingEvent.effect.energy}
                          </span>
                        </div>
                      </div>
                    )}
                    {/* Mood */}
                    {careerData.pendingEvent.effect.mood && (
                      <div className={`p-3 rounded border-2 ${careerData.pendingEvent.effect.mood > 0 ? 'bg-purple-50 border-purple-500' : 'bg-orange-50 border-orange-500'}`}>
                        <div className="font-bold text-center">
                          Mood: <span className={careerData.pendingEvent.effect.mood > 0 ? 'text-purple-600' : 'text-orange-600'}>
                            {careerData.pendingEvent.effect.mood > 0 ? '+' : ''}{careerData.pendingEvent.effect.mood}
                          </span>
                        </div>
                      </div>
                    )}
                    <button
                      onClick={() => handleEventChoice(0)}
                      disabled={isProcessingEvent}
                      className={`w-full py-2 sm:py-3 rounded-lg font-bold transition ${
                        isProcessingEvent
                          ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      {isProcessingEvent ? 'Processing...' : 'Continue'}
                    </button>
                  </div>
                )}

                {careerData.pendingEvent.type === 'choice' && (
                  <div className="space-y-3">
                    {careerData.pendingEvent.choices.map((choice, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleEventChoice(idx)}
                        disabled={isProcessingEvent}
                        className={`w-full py-2 sm:py-3 px-4 rounded-lg font-bold transition text-left ${
                          isProcessingEvent
                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {isProcessingEvent ? 'Processing...' : choice.text}
                      </button>
                    ))}
                  </div>
                )}

                {careerData.pendingEvent.type === 'battle' && (
                  <div className="space-y-3">
                    <div className="bg-red-50 p-3 rounded border-2 border-red-500">
                      <div className="font-bold text-red-700">This battle will be challenging!</div>
                      <div className="text-sm text-gray-600 mt-1">Difficulty: {careerData.pendingEvent.difficulty}x</div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => {
                          // Filter out starters and legendary Pokemon
                          // Includes both LEGENDARY_POKEMON and the "Legendary Tier" section of POKEMON
                          const legendaryNames = new Set([
                            ...Object.values(LEGENDARY_POKEMON).map(p => p.name),
                            // Legendary Tier Pokemon in main POKEMON dict
                            'Ho-Oh', 'Latias', 'Latios', 'Jirachi', 'Deoxys', 'Uxie', 'Mesprit', 'Azelf',
                            'Heatran', 'Regigigas', 'Cresselia', 'Darkrai', 'Shaymin', 'Arceus', 'Victini',
                            'Cobalion', 'Terrakion', 'Virizion', 'Mew', 'Mewtwo'
                          ]);
                          const allNonStarterPokemons = Object.values(POKEMON).filter(c => !c.isStarter && !legendaryNames.has(c.name));
                          const eventPokemon = allNonStarterPokemons[Math.floor(Math.random() * allNonStarterPokemons.length)];

                          // Send base stats and difficulty to server - let server handle scaling
                          const eventOpponent = {
                            name: eventPokemon.name,
                            trainerName: 'Wandering Champion',
                            primaryType: eventPokemon.primaryType,
                            baseStats: eventPokemon.baseStats, // Send base stats, server will scale
                            eventDifficulty: careerData.pendingEvent.difficulty, // Send difficulty multiplier
                            abilities: [...eventPokemon.defaultAbilities, ...eventPokemon.learnableAbilities.slice(0, 3), 'BodySlam'],
                            typeAptitudes: eventPokemon.typeAptitudes,
                            strategyAptitudes: eventPokemon.strategyAptitudes
                          };

                          // Store event rewards to apply after battle
                          // NOTE: Don't clear pendingEvent here - the backend needs it to validate the event battle
                          // The backend will clear it after the battle completes successfully
                          setCareerData(prev => ({
                            ...prev,
                            eventBattleRewards: careerData.pendingEvent.rewards
                          }));

                          startBattle(eventOpponent, false, false, true); // isEventBattle = true
                        }}
                        className="bg-red-600 text-white py-2 sm:py-3 rounded-lg font-bold hover:bg-red-700 transition"
                      >
                        Accept Challenge
                      </button>
                      <button
                        onClick={async () => {
                          // Decline the battle - clear event and generate training options
                          // Mark that we declined to prevent re-triggering events
                          declinedEventRef.current = true;
                          setCareerData(prev => ({
                            ...prev,
                            pendingEvent: null
                          }));
                          await requestNewTrainingOptions();
                          declinedEventRef.current = false;
                        }}
                        className="bg-gray-600 text-white py-2 sm:py-3 rounded-lg font-bold hover:bg-gray-700 transition"
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                )}

                {careerData.pendingEvent.type === 'hangout' && careerData.pendingEvent.supportName && careerData.pendingEvent.effect && (
                  <div className="space-y-3">
                    <div className="rainbow-sheen p-3 rounded text-center">
                      {(() => {
                        const { image: trainerImage, config: faceConfig, trainerName } = getSupportImageWithConfig(careerData.pendingEvent.supportName, SUPPORT_CARDS, normalizeSupportName);
                        const hasValidImage = trainerImage && !trainerImage.includes('default.png');
                        return hasValidImage && (
                          <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3 rounded-full border-4 border-yellow-400 overflow-hidden shadow-lg">
                            <img
                              src={trainerImage}
                              alt={trainerName || careerData.pendingEvent.supportName}
                              className="w-full h-full object-cover"
                              style={{
                                transform: `scale(${faceConfig.scale}) translate(${faceConfig.offsetX}%, ${faceConfig.offsetY}%)`,
                                transformOrigin: 'top center'
                              }}
                              onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                            />
                          </div>
                        );
                      })()}
                      <div className="font-bold text-gray-800 mb-2 text-lg">Special Hangout with {SUPPORT_CARDS[normalizeSupportName(careerData.pendingEvent.supportName)]?.name || careerData.pendingEvent.supportName}!</div>
                      <div className="font-semibold text-gray-700 mb-1">{careerData.pendingEvent.name}</div>
                      <div className="text-sm text-gray-600 mb-3">
                        {careerData.pendingEvent.description}
                      </div>
                    </div>
                    <button
                      onClick={() => handleEventChoice(0)}
                      disabled={isProcessingEvent}
                      className={`w-full py-2 sm:py-3 rounded-lg font-bold transition ${
                        isProcessingEvent
                          ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {isProcessingEvent ? 'Processing...' : 'Spend Time Together'}
                    </button>
                  </div>
                )}

                {/* SUPPORT CARD EVENTS (Chain and Standalone) */}
                {(careerData.pendingEvent.type === 'support_chain' || careerData.pendingEvent.type === 'support_standalone') && careerData.pendingEvent.supportName && (
                  <div className="space-y-3">
                    <div className={`p-3 rounded text-center ${
                      careerData.pendingEvent.type === 'support_chain'
                        ? 'bg-gradient-to-r from-purple-100 to-indigo-100 border-2 border-purple-300'
                        : 'bg-gradient-to-r from-amber-100 to-orange-100 border-2 border-amber-300'
                    }`}>
                      {(() => {
                        const { image: trainerImage, config: faceConfig, trainerName } = getSupportImageWithConfig(careerData.pendingEvent.supportName, SUPPORT_CARDS, normalizeSupportName);
                        const hasValidImage = trainerImage && !trainerImage.includes('default.png');
                        return hasValidImage && (
                          <div className={`w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-3 rounded-full border-4 overflow-hidden shadow-lg ${
                            careerData.pendingEvent.type === 'support_chain' ? 'border-purple-400' : 'border-amber-400'
                          }`}>
                            <img
                              src={trainerImage}
                              alt={trainerName || careerData.pendingEvent.supportName}
                              className="w-full h-full object-cover"
                              style={{
                                transform: `scale(${faceConfig.scale}) translate(${faceConfig.offsetX}%, ${faceConfig.offsetY}%)`,
                                transformOrigin: 'top center'
                              }}
                              onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                            />
                          </div>
                        );
                      })()}
                      <div className={`font-bold mb-1 text-sm ${
                        careerData.pendingEvent.type === 'support_chain' ? 'text-purple-700' : 'text-amber-700'
                      }`}>
                        {careerData.pendingEvent.type === 'support_chain'
                          ? `Story Event ${(careerData.pendingEvent.chainIndex || 0) + 1}/3`
                          : 'Special Event'
                        } - {SUPPORT_CARDS[normalizeSupportName(careerData.pendingEvent.supportName)]?.name || careerData.pendingEvent.supportName}
                      </div>
                    </div>

                    {/* Choice-type support event */}
                    {careerData.pendingEvent.eventType === 'choice' && careerData.pendingEvent.choices && (
                      <div className="space-y-2">
                        {careerData.pendingEvent.choices.map((choice, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleEventChoice(idx)}
                            disabled={isProcessingEvent}
                            className={`w-full py-2 sm:py-3 px-4 rounded-lg font-bold transition text-left ${
                              isProcessingEvent
                                ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                                : careerData.pendingEvent.type === 'support_chain'
                                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                                  : 'bg-amber-600 text-white hover:bg-amber-700'
                            }`}
                          >
                            {isProcessingEvent ? 'Processing...' : choice.text}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Stat increase type support event */}
                    {careerData.pendingEvent.eventType === 'stat_increase' && careerData.pendingEvent.effect && (
                      <div className="space-y-3">
                        <div className="bg-green-50 p-3 rounded border-2 border-green-500">
                          <div className="font-bold text-green-700 mb-2 text-center text-sm">Rewards:</div>
                          <div className="flex flex-wrap justify-center gap-2 text-xs">
                            {Object.entries(careerData.pendingEvent.effect).map(([stat, value]) => (
                              <div key={stat} className="text-center bg-white px-2 py-1 rounded">
                                <span className="text-gray-600">{stat}</span>
                                <span className="font-bold text-green-600 ml-1">+{value}</span>
                              </div>
                            ))}
                            {careerData.pendingEvent.energy && (
                              <div className="text-center bg-white px-2 py-1 rounded">
                                <span className="text-gray-600">Energy</span>
                                <span className={`font-bold ml-1 ${careerData.pendingEvent.energy > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {careerData.pendingEvent.energy > 0 ? '+' : ''}{careerData.pendingEvent.energy}
                                </span>
                              </div>
                            )}
                            {careerData.pendingEvent.skillPoints && (
                              <div className="text-center bg-white px-2 py-1 rounded">
                                <span className="text-gray-600">SP</span>
                                <span className="font-bold text-blue-600 ml-1">+{careerData.pendingEvent.skillPoints}</span>
                              </div>
                            )}
                          </div>
                          {careerData.pendingEvent.moveHint && (
                            <div className="mt-2 text-center text-xs text-yellow-700 bg-yellow-100 px-2 py-1 rounded">
                              Move Hint: {careerData.pendingEvent.moveHint}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleEventChoice(0)}
                          disabled={isProcessingEvent}
                          className={`w-full py-2 sm:py-3 rounded-lg font-bold transition ${
                            isProcessingEvent
                              ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                              : careerData.pendingEvent.type === 'support_chain'
                                ? 'bg-purple-600 text-white hover:bg-purple-700'
                                : 'bg-amber-600 text-white hover:bg-amber-700'
                          }`}
                        >
                          {isProcessingEvent ? 'Processing...' : 'Continue'}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {careerData.pendingEvent.type === 'negative' && (
                  <div className="space-y-3">
                    {/* Stat losses */}
                    {careerData.pendingEvent.effect.stats && Object.keys(careerData.pendingEvent.effect.stats).length > 0 && (
                      <div className="bg-red-50 p-3 rounded border-2 border-red-500">
                        <div className="font-bold text-red-700 mb-2 text-center">Stat Changes:</div>
                        <div className="flex flex-wrap justify-center gap-4 text-sm">
                          {Object.entries(careerData.pendingEvent.effect.stats).map(([stat, value]) => (
                            <div key={stat} className="text-center">
                              <div className="text-gray-600">{stat}</div>
                              <div className="font-bold text-red-600">{value}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Energy */}
                    {careerData.pendingEvent.effect.energy && (
                      <div className="bg-red-50 p-3 rounded border-2 border-red-500">
                        <div className="font-bold text-center">
                          Energy: <span className="text-red-600">{careerData.pendingEvent.effect.energy}</span>
                        </div>
                      </div>
                    )}
                    {/* Mood */}
                    {careerData.pendingEvent.effect.mood && (
                      <div className={`p-3 rounded border-2 ${careerData.pendingEvent.effect.mood > 0 ? 'bg-purple-50 border-purple-500' : 'bg-orange-50 border-orange-500'}`}>
                        <div className="font-bold text-center">
                          Mood: <span className={careerData.pendingEvent.effect.mood > 0 ? 'text-purple-600' : 'text-orange-600'}>
                            {careerData.pendingEvent.effect.mood > 0 ? '+' : ''}{careerData.pendingEvent.effect.mood}
                          </span>
                        </div>
                      </div>
                    )}
                    {/* Skill Points */}
                    {careerData.pendingEvent.effect.skillPoints && (
                      <div className="bg-red-50 p-3 rounded border-2 border-red-500">
                        <div className="font-bold text-center">
                          Skill Points: <span className="text-red-600">{careerData.pendingEvent.effect.skillPoints}</span>
                        </div>
                      </div>
                    )}
                    <button
                      onClick={() => handleEventChoice(0)}
                      disabled={isProcessingEvent}
                      className={`w-full py-2 sm:py-3 rounded-lg font-bold transition ${
                        isProcessingEvent
                          ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-600 text-white hover:bg-gray-700'
                      }`}
                    >
                      {isProcessingEvent ? 'Processing...' : 'Continue'}
                    </button>
                  </div>
                )}
            </motion.div>
          )}

          {/* EVENT RESULT SCREEN */}
          {careerData.eventResult && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl p-4 shadow-card border-l-4 border-pocket-green"
            >
              <h3 className="text-lg font-bold mb-3 text-pocket-green">Event Result</h3>

                {/* Flavor Text */}
                {careerData.eventResult.flavor && (
                  <div className="bg-purple-50 border-2 border-purple-500 rounded-lg p-3 mb-4">
                    <p className="text-base sm:text-lg italic text-gray-700 text-center">
                      "{careerData.eventResult.flavor}"
                    </p>
                  </div>
                )}

                {(Object.keys(careerData.eventResult.stats).length > 0 || careerData.eventResult.energy !== 0 || careerData.eventResult.skillPoints !== 0) && (
                  <div className="space-y-3">
                    {Object.keys(careerData.eventResult.stats).length > 0 && (
                      <div className={`p-3 rounded border-2 ${
                        Object.values(careerData.eventResult.stats).some(v => v < 0) ? 'bg-red-50 border-red-500' : 'bg-green-50 border-green-500'
                      }`}>
                        <div className="font-bold mb-2 text-center">Stat Changes:</div>
                        <div className="flex flex-wrap justify-center gap-4 text-sm">
                          {Object.entries(careerData.eventResult.stats).map(([stat, value]) => (
                            <div key={stat} className="text-center">
                              <div className="text-gray-600">{stat}</div>
                              <div className={`font-bold ${value >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {value >= 0 ? '+' : ''}{value}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {careerData.eventResult.energy !== 0 && (
                      <div className={`p-3 rounded border-2 ${
                        careerData.eventResult.energy >= 0 ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'
                      }`}>
                        <div className="font-bold text-center">
                          Energy: <span className={careerData.eventResult.energy >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {careerData.eventResult.energy >= 0 ? '+' : ''}{careerData.eventResult.energy}
                          </span>
                        </div>
                      </div>
                    )}

                    {careerData.eventResult.skillPoints !== 0 && (
                      <div className={`p-3 rounded border-2 ${
                        careerData.eventResult.skillPoints >= 0 ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'
                      }`}>
                        <div className="font-bold text-center">
                          Skill Points: <span className={careerData.eventResult.skillPoints >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {careerData.eventResult.skillPoints >= 0 ? '+' : ''}{careerData.eventResult.skillPoints}
                          </span>
                        </div>
                      </div>
                    )}

                    {Object.keys(careerData.eventResult.friendship).length > 0 && (
                      <div className="bg-blue-50 p-3 rounded border-2 border-blue-500">
                        <div className="font-bold mb-2">Friendship Gained:</div>
                        {Object.entries(careerData.eventResult.friendship).map(([support, value]) => (
                          <div key={support} className="text-sm">
                            <span className="font-bold">{support}:</span> <span className="text-blue-600">+{value}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {careerData.eventResult.mood !== undefined && careerData.eventResult.mood !== 0 && (
                      <div className={`p-3 rounded border-2 ${
                        careerData.eventResult.mood > 0 ? 'bg-purple-50 border-purple-500' : 'bg-orange-50 border-orange-500'
                      }`}>
                        <div className="font-bold text-center">
                          Mood: <span className={careerData.eventResult.mood > 0 ? 'text-purple-600' : 'text-orange-600'}>
                            {careerData.eventResult.mood > 0 ? '+' : ''}{careerData.eventResult.mood}
                          </span>
                        </div>
                      </div>
                    )}

                    {careerData.eventResult.moveHint && (
                      <div className="bg-yellow-50 p-3 rounded border-2 border-yellow-500">
                        <div className="font-bold text-yellow-700">💡 New Move Unlocked!</div>
                        <div className="text-sm mt-1">You can now learn: <span className="font-bold">{careerData.eventResult.moveHint}</span></div>
                      </div>
                    )}
                  </div>
                )}

                <button
                  onClick={async () => {
                    if (isProcessingEvent) return;
                    setIsProcessingEvent(true);
                    try {
                      // Server clears eventResult and returns updated state
                      // Training options are already set from resolve-event, so no need to regenerate
                      await clearEventResult();
                    } finally {
                      setIsProcessingEvent(false);
                    }
                  }}
                  disabled={isProcessingEvent}
                  className={`w-full mt-4 py-2 sm:py-3 rounded-lg font-bold transition ${
                    isProcessingEvent
                      ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                      : 'bg-purple-600 text-white hover:bg-purple-700'
                  }`}
                >
                  {isProcessingEvent ? 'Processing...' : 'Continue'}
                </button>
            </motion.div>
          )}

          {/* VIEW MODES */}
          {viewMode === 'abilities' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-3 shadow-card"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-pocket-text">Known Moves</h3>
                <button onClick={() => setViewMode('training')} className="text-sm text-pocket-blue hover:underline">
                  Back
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 sm:gap-2">
                {careerData.knownAbilities
                  .filter(moveName => MOVES[moveName])
                  .sort((a, b) => {
                    const moveA = MOVES[a];
                    const moveB = MOVES[b];
                    return moveA.type.localeCompare(moveB.type);
                  })
                  .map(moveName => {
                  const move = MOVES[moveName];
                  return (
                    <div key={moveName} className="border-2 bg-green-50 rounded p-1.5 sm:p-2" style={{ borderColor: TYPE_COLORS[move.type] }}>
                      <div className="flex justify-between items-start mb-0.5 sm:mb-1">
                        <div className="font-bold text-xs sm:text-sm truncate pr-1">{moveName}</div>
                        <TypeBadge type={move.type} size={12} className="text-[10px] sm:text-xs flex-shrink-0" />
                      </div>
                      <div className="text-[10px] sm:text-xs text-gray-500 italic mb-1">{getMoveDescription(move)}</div>
                      <div className="text-[10px] sm:text-xs text-gray-600 space-y-0.5">
                        <div>DMG: {move.damage}</div>
                        <div>Stam: {move.stamina} | CD: {move.cooldown}</div>
                        {move.effect && (
                          <div className="text-purple-600 font-bold truncate">
                            {move.effect.type === 'burn' && `Burn ${Math.round((move.effect.chance || 1) * 100)}%`}
                            {move.effect.type === 'poison' && `Poison ${Math.round((move.effect.chance || 1) * 100)}%`}
                            {move.effect.type === 'paralyze' && `Paralyze ${Math.round((move.effect.chance || 1) * 100)}%`}
                            {move.effect.type === 'stun' && `Stun ${Math.round((move.effect.chance || 1) * 100)}%`}
                            {move.effect.type === 'confuse' && `Confuse ${Math.round((move.effect.chance || 1) * 100)}%`}
                            {move.effect.type === 'freeze' && `Freeze ${Math.round((move.effect.chance || 1) * 100)}%`}
                            {move.effect.type === 'sleep' && `Sleep ${Math.round((move.effect.chance || 1) * 100)}%`}
                            {move.effect.type === 'soak' && `Soak ${Math.round((move.effect.chance || 1) * 100)}%`}
                            {move.effect.type === 'energize' && `Energize ${Math.round((move.effect.chance || 1) * 100)}%`}
                            {move.effect.type === 'drain' && `Drain ${Math.round((move.effect.healPercent || 0.5) * 100)}%`}
                            {move.effect.type === 'recoil' && `Recoil ${Math.round((move.effect.damagePercent || 0.1) * 100)}%`}
                            {move.effect.type === 'exhaust' && `Self-Exhaust`}
                            {move.effect.type === 'evasion' && `Evasion`}
                            {move.effect.type === 'high_crit' && `High Crit`}
                            {move.effect.type === 'buff_speed' && `+Speed`}
                            {move.effect.type === 'buff_attack' && `+Attack`}
                            {move.effect.type === 'buff_defense' && `+Defense`}
                            {move.effect.type === 'buff_instinct' && `+Instinct`}
                            {move.effect.type === 'buff_attack_defense' && `+Atk/Def`}
                            {move.effect.type === 'buff_attack_speed' && `+Atk/Spd`}
                            {move.effect.type === 'debuff_attack' && `-Attack`}
                            {move.effect.type === 'debuff_defense' && `-Defense`}
                            {move.effect.type === 'debuff_speed' && `-Speed`}
                            {move.effect.type === 'debuff_instinct' && `-Instinct`}
                            {move.effect.type === 'debuff_accuracy' && `-Accuracy`}
                            {move.effect.type === 'debuff_instinct_self' && `Self -Inst`}
                            {move.effect.type === 'debuff_speed_self' && `Self -Spd`}
                            {move.effect.type === 'debuff_attack_self' && `Self -Atk`}
                            {move.effect.type === 'heal_self' && `Heal ${Math.round((move.effect.healPercent || 0.5) * 100)}%`}
                            {move.effect.type === 'rest' && `Full Heal + Sleep ${move.effect.sleepDuration}t`}
                            {move.effect.type === 'badly_poison' && `Bad Poison`}
                            {move.effect.type === 'regen' && `Regen`}
                            {move.effect.type === 'weather_rain' && `Rain: +50% Water`}
                            {move.effect.type === 'weather_sun' && `Sun: +50% Fire`}
                            {move.effect.type === 'weather_sand' && `Sandstorm: Chip DMG`}
                            {move.effect.type === 'weather_hail' && `Hail: Chip DMG`}
                            {move.effect.type === 'terrain_electric' && `Terrain: +50% Electric`}
                            {move.effect.type === 'terrain_grassy' && `Terrain: +50% Grass`}
                            {move.effect.type === 'terrain_psychic' && `Terrain: +50% Psychic`}
                          </div>
                        )}
                      </div>
                      {forgetMoveConfirm === moveName ? (
                        <div className="flex gap-1 mt-1">
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              // Use server-authoritative forget ability
                              await forgetAbilityOnServer(moveName);
                              setForgetMoveConfirm(null);
                            }}
                            className="flex-1 bg-red-600 text-white text-[10px] sm:text-xs py-0.5 sm:py-1 rounded hover:bg-red-700 cursor-pointer"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setForgetMoveConfirm(null);
                            }}
                            className="flex-1 bg-gray-400 text-white text-[10px] sm:text-xs py-0.5 sm:py-1 rounded hover:bg-gray-500 cursor-pointer"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setForgetMoveConfirm(moveName);
                          }}
                          className="w-full mt-1 bg-red-500 text-white text-[10px] sm:text-xs py-0.5 sm:py-1 rounded hover:bg-red-600 cursor-pointer"
                        >
                          Forget
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {viewMode === 'gym' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-3 shadow-card"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-pocket-text">Next Gym Leader</h3>
                <button onClick={() => setViewMode('training')} className="text-sm text-pocket-blue hover:underline">
                  Back to Training
                </button>
              </div>
              {nextGymLeader && nextGymLeader.pokemon && (() => {
                const gymScaledStats = getScaledStats(nextGymLeader.pokemon.baseStats, nextGymTurn);
                const gymEvolutionDisplayName = getEvolutionFormForGrade(nextGymLeader.pokemon.name, gymScaledStats);
                return (
                <div className="border-2 border-yellow-500 rounded-lg p-3">
                  <div className="flex items-center gap-2 sm:gap-3 mb-3">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg border-2 border-yellow-400 overflow-hidden bg-gray-800 flex-shrink-0">
                      <img
                        src={getGymLeaderImage(nextGymLeader.name)}
                        alt={nextGymLeader.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="flex-shrink-0">
                      {generatePokemonSprite(nextGymLeader.pokemon.primaryType, gymEvolutionDisplayName, 56, false)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-lg font-bold">{nextGymLeader.name}</h4>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        {gymEvolutionDisplayName} <TypeBadge type={nextGymLeader.pokemon.primaryType} size={12} />
                      </p>
                      {(() => {
                        const derivedStrategy = getBestStrategy(nextGymLeader.pokemon.strategyAptitudes);
                        return derivedStrategy ? (
                          <p className="text-xs text-gray-500 mt-1">Strategy: {derivedStrategy.name} ({derivedStrategy.grade})</p>
                        ) : (
                          <p className="text-xs text-gray-500 mt-1">Strategy: Chipper (C)</p>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs mb-3">
                    <span className="flex items-center gap-0.5"><StatIcon stat="HP" size={12} /><span className="font-bold">{gymScaledStats.HP}</span></span>
                    <span className="flex items-center gap-0.5"><StatIcon stat="Attack" size={12} /><span className="font-bold">{gymScaledStats.Attack}</span></span>
                    <span className="flex items-center gap-0.5"><StatIcon stat="Defense" size={12} /><span className="font-bold">{gymScaledStats.Defense}</span></span>
                    <span className="flex items-center gap-0.5"><StatIcon stat="Instinct" size={12} /><span className="font-bold">{gymScaledStats.Instinct}</span></span>
                    <span className="flex items-center gap-0.5"><StatIcon stat="Speed" size={12} /><span className="font-bold">{gymScaledStats.Speed}</span></span>
                  </div>
                  <div className="mb-2">
                    <div className="font-bold text-sm mb-2">Moves:</div>
                    <div className="grid grid-cols-2 gap-2">
                      {(() => {
                        const allMoves = [...(nextGymLeader.pokemon.defaultAbilities || []), ...(nextGymLeader.pokemon.learnableAbilities || [])];
                        const moveCount = GYM_LEADER_MOVE_COUNTS[careerData.currentGymIndex] || 5;
                        return allMoves.slice(0, moveCount);
                      })().map(moveName => {
                        const move = MOVES[moveName];
                        if (!move) return null;
                        return (
                          <div key={moveName} className="border rounded p-2 bg-gray-50">
                            <div className="flex justify-between items-start mb-1">
                              <div className="font-bold text-xs">{moveName}</div>
                              <TypeBadge type={move.type} size={12} className="text-xs" />
                            </div>
                            <div className="text-[10px] text-gray-500 italic mb-1">{getMoveDescription(move)}</div>
                            <div className="text-xs text-gray-600 space-y-0.5">
                              <div>DMG: {move.damage}</div>
                              <div>Stam: {move.stamina} | CD: {move.cooldown}</div>
                            </div>
                            {move.effect && (
                              <div className="text-[10px] text-purple-600 font-bold mt-1">
                                {move.effect.type === 'burn' && `Burn ${Math.round((move.effect.chance || 1) * 100)}%`}
                                {move.effect.type === 'poison' && `Poison ${Math.round((move.effect.chance || 1) * 100)}%`}
                                {move.effect.type === 'paralyze' && `Paralyze ${Math.round((move.effect.chance || 1) * 100)}%`}
                                {move.effect.type === 'stun' && `Stun ${Math.round((move.effect.chance || 1) * 100)}%`}
                                {move.effect.type === 'confuse' && `Confuse ${Math.round((move.effect.chance || 1) * 100)}%`}
                                {move.effect.type === 'freeze' && `Freeze ${Math.round((move.effect.chance || 1) * 100)}%`}
                                {move.effect.type === 'sleep' && `Sleep ${Math.round((move.effect.chance || 1) * 100)}%`}
                                {move.effect.type === 'soak' && `Soak ${Math.round((move.effect.chance || 1) * 100)}%`}
                                {move.effect.type === 'energize' && `Energize ${Math.round((move.effect.chance || 1) * 100)}%`}
                                {move.effect.type === 'drain' && `Drain ${Math.round((move.effect.healPercent || 0.5) * 100)}%`}
                                {move.effect.type === 'recoil' && `Recoil ${Math.round((move.effect.damagePercent || 0.1) * 100)}%`}
                                {move.effect.type === 'exhaust' && `Self-Exhaust`}
                                {move.effect.type === 'evasion' && `Evasion`}
                                {move.effect.type === 'high_crit' && `High Crit`}
                                {move.effect.type === 'buff_speed' && `+Speed`}
                                {move.effect.type === 'buff_attack' && `+Attack`}
                                {move.effect.type === 'buff_defense' && `+Defense`}
                                {move.effect.type === 'buff_instinct' && `+Instinct`}
                                {move.effect.type === 'buff_attack_defense' && `+Atk/Def`}
                                {move.effect.type === 'buff_attack_speed' && `+Atk/Spd`}
                                {move.effect.type === 'debuff_attack' && `-Attack`}
                                {move.effect.type === 'debuff_defense' && `-Defense`}
                                {move.effect.type === 'debuff_speed' && `-Speed`}
                                {move.effect.type === 'debuff_instinct' && `-Instinct`}
                                {move.effect.type === 'debuff_accuracy' && `-Accuracy`}
                                {move.effect.type === 'debuff_instinct_self' && `Self -Inst`}
                                {move.effect.type === 'debuff_speed_self' && `Self -Spd`}
                                {move.effect.type === 'debuff_attack_self' && `Self -Atk`}
                                {move.effect.type === 'heal_self' && `Heal ${Math.round((move.effect.healPercent || 0.5) * 100)}%`}
                                {move.effect.type === 'rest' && `Full Heal + Sleep ${move.effect.sleepDuration}t`}
                                {move.effect.type === 'badly_poison' && `Bad Poison`}
                                {move.effect.type === 'regen' && `Regen`}
                                {move.effect.type === 'weather_rain' && `Rain: +50% Water`}
                                {move.effect.type === 'weather_sun' && `Sun: +50% Fire`}
                                {move.effect.type === 'weather_sand' && `Sandstorm: Chip DMG`}
                                {move.effect.type === 'weather_hail' && `Hail: Chip DMG`}
                                {move.effect.type === 'terrain_electric' && `Terrain: +50% Electric`}
                                {move.effect.type === 'terrain_grassy' && `Terrain: +50% Grass`}
                                {move.effect.type === 'terrain_psychic' && `Terrain: +50% Psychic`}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
              })()}
            </motion.div>
          )}

          {viewMode === 'learn' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-3 shadow-card"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                <h3 className="font-bold text-pocket-text">Learn Moves ({careerData.skillPoints ?? 0} SP)</h3>
                <button onClick={() => setViewMode('training')} className="text-sm text-pocket-blue hover:underline self-start sm:self-auto">
                  Back
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 sm:gap-2">
                {(() => {
                  // Combine learnableAbilities with any hinted moves that aren't already included
                  const baseLearnables = careerData.pokemon.learnableAbilities || [];
                  const hintedMoves = Object.keys(careerData.moveHints || {});
                  const knownMoves = careerData.knownAbilities || [];

                  // Add hinted moves that aren't in base list and aren't already known
                  const additionalFromHints = hintedMoves.filter(
                    move => !baseLearnables.includes(move) && !knownMoves.includes(move) && MOVES[move]
                  );

                  const allLearnables = [...baseLearnables, ...additionalFromHints];

                  // Debug logging
                  if (additionalFromHints.length > 0) {
                    console.log('[Learn Screen] Adding hinted moves not in base list:', additionalFromHints);
                  }

                  return allLearnables;
                })()
                  .filter(moveName => {
                    if (!MOVES[moveName]) {
                      console.error(`[Learn Screen] Move not found: ${moveName}`);
                      return false;
                    }
                    return true;
                  })
                  .sort((a, b) => {
                    try {
                      const moveA = MOVES[a];
                      const moveB = MOVES[b];
                      if (!moveA || !moveB) {
                        console.error('[Learn Screen] Missing move data:', { a, moveA, b, moveB });
                        return 0;
                      }
                      return (moveA.type || '').localeCompare(moveB.type || '');
                    } catch (error) {
                      console.error('[Learn Screen] Sort error:', error, { a, b });
                      return 0;
                    }
                  })
                  .map(moveName => {
                    try {
                      const move = MOVES[moveName];
                      if (!move) {
                        console.error(`[Learn Screen] Move data missing for: ${moveName}`);
                        return null;
                      }

                      const isKnown = careerData.knownAbilities.includes(moveName);
                      // moveHints stores the accumulated discount value directly (e.g., 0.15, 0.30, etc.)
                      const discount = (careerData.moveHints || {})[moveName] || 0;
                      const finalCost = Math.ceil(move.cost * (1 - discount));
                      const canAfford = (careerData.skillPoints ?? 0) >= finalCost;
                      const isStabMove = move.type === careerData.pokemon.primaryType;

                      return (
                        <div key={moveName} className={`border-2 rounded p-1.5 sm:p-2 ${isKnown ? 'bg-green-50' : ''} ${!isKnown && canAfford ? 'tutorial-affordable-move' : ''} ${!isKnown && canAfford && isStabMove ? 'tutorial-stab-move' : ''}`} style={{ borderColor: isKnown ? '#22c55e' : TYPE_COLORS[move.type] }}>
                          <div className="flex justify-between items-start mb-0.5 sm:mb-1">
                            <div className="font-bold text-xs sm:text-sm truncate pr-1">{moveName}</div>
                            <TypeBadge type={move.type} size={12} className="text-[10px] sm:text-xs flex-shrink-0" />
                          </div>
                          <div className="text-[10px] sm:text-xs text-gray-500 italic mb-1">{getMoveDescription(move)}</div>
                          <div className="text-[10px] sm:text-xs text-gray-600 space-y-0.5 mb-1 sm:mb-2">
                            <div>DMG: {move.damage}</div>
                            <div>Stam: {move.stamina} | CD: {move.cooldown}</div>
                            {move.effect && (
                              <div className="text-purple-600 font-bold truncate">
                                {move.effect.type === 'burn' && `Burn ${Math.round((move.effect.chance || 1) * 100)}%`}
                                {move.effect.type === 'poison' && `Poison ${Math.round((move.effect.chance || 1) * 100)}%`}
                                {move.effect.type === 'paralyze' && `Paralyze ${Math.round((move.effect.chance || 1) * 100)}%`}
                                {move.effect.type === 'stun' && `Stun ${Math.round((move.effect.chance || 1) * 100)}%`}
                                {move.effect.type === 'confuse' && `Confuse ${Math.round((move.effect.chance || 1) * 100)}%`}
                                {move.effect.type === 'freeze' && `Freeze ${Math.round((move.effect.chance || 1) * 100)}%`}
                                {move.effect.type === 'sleep' && `Sleep ${Math.round((move.effect.chance || 1) * 100)}%`}
                                {move.effect.type === 'soak' && `Soak ${Math.round((move.effect.chance || 1) * 100)}%`}
                                {move.effect.type === 'energize' && `Energize ${Math.round((move.effect.chance || 1) * 100)}%`}
                                {move.effect.type === 'drain' && `Drain ${Math.round((move.effect.healPercent || 0.5) * 100)}%`}
                                {move.effect.type === 'recoil' && `Recoil ${Math.round((move.effect.damagePercent || 0.1) * 100)}%`}
                                {move.effect.type === 'exhaust' && `Self-Exhaust`}
                                {move.effect.type === 'evasion' && `Evasion`}
                                {move.effect.type === 'high_crit' && `High Crit`}
                                {move.effect.type === 'buff_speed' && `+Speed`}
                                {move.effect.type === 'buff_attack' && `+Attack`}
                                {move.effect.type === 'buff_defense' && `+Defense`}
                                {move.effect.type === 'buff_instinct' && `+Instinct`}
                                {move.effect.type === 'buff_attack_defense' && `+Atk/Def`}
                                {move.effect.type === 'buff_attack_speed' && `+Atk/Spd`}
                                {move.effect.type === 'debuff_attack' && `-Attack`}
                                {move.effect.type === 'debuff_defense' && `-Defense`}
                                {move.effect.type === 'debuff_speed' && `-Speed`}
                                {move.effect.type === 'debuff_instinct' && `-Instinct`}
                                {move.effect.type === 'debuff_accuracy' && `-Accuracy`}
                                {move.effect.type === 'debuff_instinct_self' && `Self -Inst`}
                                {move.effect.type === 'debuff_speed_self' && `Self -Spd`}
                                {move.effect.type === 'debuff_attack_self' && `Self -Atk`}
                                {move.effect.type === 'heal_self' && `Heal ${Math.round((move.effect.healPercent || 0.5) * 100)}%`}
                                {move.effect.type === 'rest' && `Full Heal + Sleep ${move.effect.sleepDuration}t`}
                                {move.effect.type === 'badly_poison' && `Bad Poison`}
                                {move.effect.type === 'weather_rain' && `Rain: +50% Water`}
                                {move.effect.type === 'weather_sun' && `Sun: +50% Fire`}
                                {move.effect.type === 'weather_sand' && `Sandstorm: Chip DMG`}
                                {move.effect.type === 'weather_hail' && `Hail: Chip DMG`}
                                {move.effect.type === 'terrain_electric' && `Terrain: +50% Electric`}
                                {move.effect.type === 'terrain_grassy' && `Terrain: +50% Grass`}
                                {move.effect.type === 'terrain_psychic' && `Terrain: +50% Psychic`}
                                {move.effect.type === 'buff_crit' && `+Crit Rate`}
                              </div>
                            )}
                          </div>
                          {!isKnown && (
                            <>
                              {discount > 0 && (
                                <div className="text-[10px] sm:text-xs text-green-600 font-bold mb-0.5 sm:mb-1">
                                  -{Math.round(discount * 100)}% off!
                                </div>
                              )}
                              <button
                                onClick={() => learnMove(moveName)}
                                disabled={!canAfford}
                                className={`w-full py-1 rounded text-[10px] sm:text-xs font-bold ${
                                  canAfford ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                              >
                                Learn ({finalCost} SP)
                              </button>
                            </>
                          )}
                          {isKnown && <div className="text-center text-green-600 font-bold text-[10px] sm:text-xs">Learned</div>}
                        </div>
                      );
                    } catch (error) {
                      console.error('[Learn Screen] Render error for move:', moveName, error);
                      return null;
                    }
                })}
              </div>
            </motion.div>
          )}

          {/* TRAINING VIEW */}
          {viewMode === 'training' && careerData.currentTrainingOptions && !careerData.pendingEvent && !careerData.eventResult && !isBattleTurn && !inspirationModal && (
            <>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="pokeball-lower rounded-2xl p-3 shadow-lg overflow-hidden"
              >
                <div className="relative z-10 pt-1">
                  {/* Mobile: Stack header elements vertically for better fit */}
                  <div className="mb-3">
                    {/* Row 1: Title + Energy + Mood */}
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <h3 className="font-bold text-pokeball-black text-base sm:text-lg flex-shrink-0">Training</h3>
                        {/* Energy bar */}
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] sm:text-xs font-bold text-pokeball-black-light">Energy</span>
                          <div className="relative w-20 sm:w-40 h-5 sm:h-6 rounded-full overflow-hidden bg-gray-200 border-2 border-pokeball-black/20 shadow-inner">
                            {/* Active energy portion with conditional color */}
                            <div
                              className="absolute top-0 left-0 h-full rounded-full transition-all duration-300"
                              style={{
                                width: `${Math.min(100, (careerData.energy / (careerData.maxEnergy || GAME_CONFIG.CAREER?.MAX_ENERGY || 100)) * 100)}%`,
                                background: careerData.energy > 50
                                  ? 'linear-gradient(90deg, #22C55E 0%, #16A34A 100%)'
                                  : careerData.energy >= 25
                                    ? 'linear-gradient(90deg, #FBBF24 0%, #F59E0B 100%)'
                                    : 'linear-gradient(90deg, #DC2626 0%, #B91C1C 100%)'
                              }}
                            />
                            {/* Energy text overlay */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-[9px] sm:text-xs font-bold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                                {careerData.energy}/{careerData.maxEnergy || GAME_CONFIG.CAREER?.MAX_ENERGY || 100}
                              </span>
                            </div>
                          </div>
                        </div>
                        {/* Mood indicator - Purple (Awful) to Pink (Great) */}
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs sm:text-sm font-bold text-pokeball-black-light">Mood</span>
                          <div className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-bold ${
                            (careerData.mood ?? 2) === 4 ? 'bg-pink-400 text-white' :
                            (careerData.mood ?? 2) === 3 ? 'bg-orange-400 text-white' :
                            (careerData.mood ?? 2) === 2 ? 'bg-yellow-300 text-yellow-800' :
                            (careerData.mood ?? 2) === 1 ? 'bg-blue-400 text-white' :
                            'bg-purple-500 text-white'
                          }`}>
                            {['Awful', 'Bad', 'Normal', 'Good', 'Great'][careerData.mood ?? 2]}
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Row 2: Action buttons - full width grid on mobile */}
                    <div className="grid grid-cols-4 gap-1 sm:flex sm:gap-2 sm:justify-end">
                      <button
                        onClick={performRest}
                        disabled={isProcessingAction}
                        className={`tutorial-rest-button px-1 sm:px-4 py-1.5 sm:py-2 rounded-lg font-bold transition text-[10px] sm:text-sm shadow-md ${
                          isProcessingAction
                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                            : 'bg-gradient-to-b from-green-500 to-green-600 text-white hover:from-green-400 hover:to-green-500 border-2 border-green-700'
                        }`}
                      >
                        Rest
                      </button>
                      <button
                        onClick={performRelax}
                        disabled={isProcessingAction}
                        className={`tutorial-relax-button px-1 sm:px-4 py-1.5 sm:py-2 rounded-lg font-bold transition text-[10px] sm:text-sm shadow-md ${
                          isProcessingAction
                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                            : 'bg-gradient-to-b from-purple-500 to-purple-600 text-white hover:from-purple-400 hover:to-purple-500 border-2 border-purple-700'
                        }`}
                      >
                        Relax
                      </button>
                      <button
                        onClick={() => setViewMode('battle')}
                        className="tutorial-battle-tab px-1 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-b from-pokeball-red-light to-pokeball-red text-white rounded-lg font-bold hover:from-pokeball-red hover:to-pokeball-red-dark transition text-[10px] sm:text-sm shadow-md border-2 border-pokeball-red-dark"
                      >
                        Battle
                      </button>
                      <button
                        onClick={() => setViewMode('log')}
                        className="px-1 sm:px-4 py-1.5 sm:py-2 bg-white text-pokeball-black border-2 border-pokeball-black/20 rounded-lg font-bold hover:border-pokeball-black/40 hover:bg-gray-50 transition text-[10px] sm:text-sm shadow-md"
                      >
                        Log
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-5 gap-1 sm:gap-2">
                    {['HP', 'Attack', 'Defense', 'Instinct', 'Speed'].map(stat => {
                      const option = careerData.currentTrainingOptions[stat];
                      if (!option) return null; // Skip if option doesn't exist for this stat

                      // Use preview values calculated by backend
                      const statGain = option.preview?.statGain || 0;
                      const energyCost = option.preview?.energyCost || 0;
                      const energyRegenBonus = option.preview?.energyRegenBonus || 0;
                      const failChance = option.preview?.failChance || 0;

                      // Get training progress and level
                      const trainingLevel = careerData.trainingLevels?.[stat] || 0;
                      const trainingProgress = careerData.trainingProgress?.[stat] || 0;

                      // Get training level color based on level (1-5 display, matching rarity colors)
                      // Internal level 0-4 maps to display level 1-5
                      const getLevelColor = (level) => {
                        const displayLevel = Math.min(level + 1, 5); // Cap at 5
                        if (displayLevel === 1) return '#9ca3af'; // Gray (Common)
                        if (displayLevel === 2) return '#22c55e'; // Green (Uncommon)
                        if (displayLevel === 3) return '#3b82f6'; // Blue (Rare)
                        if (displayLevel === 4) return '#a855f7'; // Purple (Legendary-ish)
                        return '#eab308'; // Yellow/Gold (Max level)
                      };

                      // Display level (1-5, capped)
                      const displayLevel = Math.min(trainingLevel + 1, 5);

                      const currentStatValue = careerData.currentStats[stat];

                      // Check if any support card's type matches this stat AND is at max friendship
                      const hasMaxFriendshipTypeMatch = option.supports.some(supportName => {
                        const support = getSupportCardAttributes(supportName);
                        if (!support) return false;
                        const supportType = support.supportType;
                        const friendship = careerData.supportFriendships?.[supportName] || 0;
                        const isMaxFriendship = friendship >= 100;
                        const matches = supportType === stat && isMaxFriendship;
                        if (isMaxFriendship) {
                          console.log('[RainbowSheen] Support:', supportName, 'Type:', supportType, 'Stat:', stat, 'Friendship:', friendship, 'Matches:', matches);
                        }
                        return matches;
                      });

                      return (
                        <button
                          key={stat}
                          onClick={() => performTraining(stat)}
                          disabled={isProcessingAction}
                          className={`rounded-xl p-1.5 sm:p-2 text-left transition relative overflow-hidden shadow-md ${
                            isProcessingAction
                              ? 'bg-gray-200 cursor-not-allowed opacity-60 border-2 border-gray-300'
                              : hasMaxFriendshipTypeMatch
                                ? 'border-2 border-amber-400 cursor-pointer'
                                : 'pokeball-training-card hover:shadow-lg hover:scale-[1.02] cursor-pointer'
                          }`}
                          style={hasMaxFriendshipTypeMatch ? {
                            background: 'linear-gradient(135deg, rgba(255,180,180,0.5) 0%, rgba(255,255,180,0.5) 20%, rgba(180,255,180,0.5) 40%, rgba(180,255,255,0.5) 60%, rgba(180,180,255,0.5) 80%, rgba(255,180,255,0.5) 100%)',
                            animation: 'rainbow-sheen 3s ease infinite',
                            backgroundSize: '200% 200%',
                            boxShadow: '0 0 12px rgba(255, 200, 100, 0.6)'
                          } : {}}
                        >
                          <div className="flex items-center justify-between mb-0.5 sm:mb-1">
                            <div className="font-bold text-[10px] sm:text-sm text-pokeball-black">{stat}</div>
                            <div
                              className="text-white text-[8px] sm:text-xs px-1.5 py-0.5 rounded-md font-bold shadow-sm"
                              style={{ backgroundColor: getLevelColor(trainingLevel) }}
                            >
                              Lv.{displayLevel}
                            </div>
                          </div>
                          <div className="text-[9px] sm:text-xs mb-0.5 sm:mb-1">
                            <span className="text-pokeball-black-light">{currentStatValue}</span>
                            <span className="text-green-600 font-bold"> +{statGain}</span>
                          </div>
                          <div className="text-[9px] sm:text-xs text-pokeball-black-light mb-0.5 sm:mb-1">
                            {energyCost > 0 ? `-${energyCost}` : `+${Math.abs(energyCost)}`}{energyRegenBonus > 0 ? <span className="text-green-600 font-bold">+{energyRegenBonus}</span> : ''} Energy
                          </div>
                          <div className="text-[9px] sm:text-xs font-bold mb-0.5 sm:mb-1" style={{ color: failChance === 0 ? '#16A34A' : failChance <= 25 ? '#F59E0B' : '#DC2626' }}>
                            Fail: {failChance}%
                          </div>
                          {/* Training progress bar */}
                          <div className="mb-0.5 sm:mb-1">
                            <div className="flex items-center justify-between text-[8px] sm:text-xs text-pokeball-black-light mb-0.5">
                              <span>Progress</span>
                              <span>{trainingProgress}/4</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5 border border-gray-300">
                              <div
                                className="h-full rounded-full transition-all"
                                style={{
                                  width: `${(trainingProgress / 4) * 100}%`,
                                  backgroundColor: getLevelColor(trainingLevel)
                                }}
                              />
                            </div>
                          </div>
                          {option.supports && option.supports.length > 0 && (
                            <div className="space-y-0.5">
                              {option.supports.map(supportName => {
                                // Use stored friendship, or fall back to initial friendship from support card
                                const support = getSupportCardAttributes(supportName);
                                const initialFriendship = support?.initialFriendship || 0;
                                const friendship = careerData.supportFriendships?.[supportName] ?? initialFriendship;
                                const { image: trainerImage, config: faceConfig, trainerName } = getSupportImageWithConfig(supportName, SUPPORT_CARDS, normalizeSupportName);
                                // Only show image if we found a valid trainer in our image mappings
                                const hasValidImage = trainerImage && !trainerImage.includes('default.png');
                                return (
                                  <div key={supportName} className="bg-blue-50 text-pokeball-black text-[8px] sm:text-xs px-0.5 sm:px-1 py-0.5 rounded-lg flex items-center gap-1 border border-blue-300">
                                    {hasValidImage ? (
                                      <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg border-2 border-blue-400 overflow-hidden flex-shrink-0 bg-white">
                                        <img
                                          src={trainerImage}
                                          alt={trainerName || supportName}
                                          className="w-full h-full object-cover"
                                          style={{
                                            transform: `scale(${faceConfig.scale}) translate(${faceConfig.offsetX}%, ${faceConfig.offsetY}%)`,
                                            transformOrigin: 'top center'
                                          }}
                                          onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                      </div>
                                    ) : (
                                      <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg border-2 border-blue-400 bg-blue-100 flex items-center justify-center flex-shrink-0">
                                        <span className="text-[8px] font-bold text-blue-600">{(support?.name || supportName).charAt(0)}</span>
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <div className="font-bold truncate">{support?.name || supportName}</div>
                                      <div className="flex items-center gap-0.5 sm:gap-1">
                                        <div className="flex-1 bg-gray-200 rounded-full h-1 min-w-0 border border-gray-300">
                                          <div className="bg-blue-500 h-full rounded-full" style={{ width: `${friendship}%` }} />
                                        </div>
                                        <span className="text-[8px] sm:text-xs text-pokeball-black-light">{friendship}</span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {/* Display hints - support both old (hint) and new (hints) format */}
                          {(option.hints?.length > 0 || option.hint) && (
                            <div className="flex flex-wrap gap-0.5 mt-0.5 sm:mt-1">
                              {(option.hints || [option.hint]).map((hint, idx) => (
                                <div key={idx} className="bg-yellow-100 text-yellow-800 text-[8px] sm:text-xs px-1 sm:px-1.5 py-0.5 rounded-lg truncate border border-yellow-300 shadow-sm">
                                  💡 {hint.move}
                                </div>
                              ))}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>

              {careerData.turnLog.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="pokeball-card rounded-2xl p-3 shadow-lg"
                >
                  <h3 className="font-bold text-pokeball-black text-sm mb-2">Last Turn Result</h3>
                  {(() => {
                    const entry = careerData.turnLog[0];
                    return (
                      <div
                        className={`p-2.5 rounded-xl text-sm shadow-sm ${
                          entry.type === 'training_success' ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-600' :
                          entry.type === 'training_levelup' ? 'bg-gradient-to-r from-purple-50 to-violet-50 border-l-4 border-purple-500' :
                          entry.type === 'training_fail' ? 'bg-gradient-to-r from-red-50 to-rose-50 border-l-4 border-pokeball-red' :
                          entry.type === 'battle_victory' || entry.type === 'gym_victory' ? 'bg-gradient-to-r from-blue-50 to-sky-50 border-l-4 border-blue-500' :
                          entry.type === 'battle_loss' ? 'bg-gradient-to-r from-orange-50 to-amber-50 border-l-4 border-orange-500' :
                          'bg-gradient-to-r from-gray-50 to-slate-50 border-l-4 border-gray-500'
                        }`}
                      >
                        <div className="font-bold text-xs text-pokeball-black-light mb-1">Turn {entry.turn}</div>
                        <div className="text-pokeball-black">{entry.message}</div>
                        {entry.moodLoss && (
                          <div className="text-xs text-orange-600 mt-1 font-medium">
                            Mood: -{entry.moodLoss}
                          </div>
                        )}
                        {entry.moodGain && entry.moodGain > 0 && (
                          <div className="text-xs text-purple-600 mt-1 font-medium">
                            Mood: +{entry.moodGain}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </motion.div>
              )}
            </>
          )}

          {/* BATTLE VIEW */}
          {viewMode === 'battle' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="pokeball-lower rounded-2xl p-3 shadow-lg overflow-hidden"
            >
              <div className="relative z-10 pt-1">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-pokeball-black text-lg">Wild Battles</h3>
                  <button onClick={() => setViewMode('training')} className="text-sm text-pokeball-black font-bold hover:text-pokeball-red transition px-3 py-1 bg-white rounded-lg border-2 border-pokeball-black/20 hover:border-pokeball-red/50">
                    Back to Training
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {careerData.availableBattles.map((opponent, idx) => {
                    const evolutionDisplayName = getEvolutionFormForGrade(opponent.name, opponent.stats);
                    return (
                    <div key={idx} className="bg-white border-2 border-pokeball-red rounded-xl p-2.5 shadow-md hover:shadow-lg transition">
                      <div className="flex items-start gap-2 mb-2">
                        <div className="flex-shrink-0">
                          {generatePokemonSprite(opponent.primaryType, evolutionDisplayName, 64, false)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className="font-bold text-sm text-pokeball-black">{evolutionDisplayName}</span>
                            <span
                              className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white"
                              style={{ backgroundColor: getGradeColor(getPokemonGrade(opponent.stats)) }}
                            >
                              {getPokemonGrade(opponent.stats)}
                            </span>
                          </div>
                          <div className="text-xs text-pokeball-black-light flex items-center gap-1">
                            <TypeBadge type={opponent.primaryType} size={12} />
                            <span>{opponent.strategy} ({opponent.strategyGrade})</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] mb-2">
                        <span className="flex items-center gap-0.5"><StatIcon stat="HP" size={10} /><span className="font-bold text-pokeball-black">{opponent.stats.HP}</span></span>
                        <span className="flex items-center gap-0.5"><StatIcon stat="Attack" size={10} /><span className="font-bold text-pokeball-black">{opponent.stats.Attack}</span></span>
                        <span className="flex items-center gap-0.5"><StatIcon stat="Defense" size={10} /><span className="font-bold text-pokeball-black">{opponent.stats.Defense}</span></span>
                        <span className="flex items-center gap-0.5"><StatIcon stat="Instinct" size={10} /><span className="font-bold text-pokeball-black">{opponent.stats.Instinct}</span></span>
                        <span className="flex items-center gap-0.5"><StatIcon stat="Speed" size={10} /><span className="font-bold text-pokeball-black">{opponent.stats.Speed}</span></span>
                      </div>
                      <div className="mb-2">
                        <div className="font-bold text-xs mb-1 text-pokeball-black">Moves:</div>
                        <div className="space-y-1">
                          {opponent.abilities.map(moveName => {
                            const move = MOVES[moveName];
                            if (!move) return null;
                            return (
                              <div key={moveName} className="bg-gray-100 rounded-lg p-1.5 text-xs border border-gray-200">
                                <div className="flex justify-between items-center mb-0.5">
                                  <span className="font-bold text-pokeball-black">{moveName}</span>
                                  <TypeBadge type={move.type} size={10} className="text-[10px]" />
                                </div>
                                <div className="text-[10px] text-gray-500 italic mb-0.5">{getMoveDescription(move)}</div>
                                <div className="text-[10px] text-pokeball-black-light">
                                  <div>DMG: {move.damage}</div>
                                  <div>Stam: {move.stamina} | CD: {move.cooldown}</div>
                                </div>
                                {move.effect && (
                                  <div className="text-[10px] text-purple-600 font-bold mt-0.5">
                                    {move.effect.type === 'burn' && `Burn ${Math.round((move.effect.chance || 1) * 100)}%`}
                                    {move.effect.type === 'poison' && `Poison ${Math.round((move.effect.chance || 1) * 100)}%`}
                                    {move.effect.type === 'paralyze' && `Paralyze ${Math.round((move.effect.chance || 1) * 100)}%`}
                                    {move.effect.type === 'stun' && `Stun ${Math.round((move.effect.chance || 1) * 100)}%`}
                                    {move.effect.type === 'confuse' && `Confuse ${Math.round((move.effect.chance || 1) * 100)}%`}
                                    {move.effect.type === 'freeze' && `Freeze ${Math.round((move.effect.chance || 1) * 100)}%`}
                                    {move.effect.type === 'sleep' && `Sleep ${Math.round((move.effect.chance || 1) * 100)}%`}
                                    {move.effect.type === 'soak' && `Soak ${Math.round((move.effect.chance || 1) * 100)}%`}
                                    {move.effect.type === 'energize' && `Energize ${Math.round((move.effect.chance || 1) * 100)}%`}
                                    {move.effect.type === 'drain' && `Drain ${Math.round((move.effect.healPercent || 0.5) * 100)}%`}
                                    {move.effect.type === 'recoil' && `Recoil ${Math.round((move.effect.damagePercent || 0.1) * 100)}%`}
                                    {move.effect.type === 'exhaust' && `Self-Exhaust`}
                                    {move.effect.type === 'evasion' && `Evasion`}
                                    {move.effect.type === 'high_crit' && `High Crit`}
                                    {move.effect.type === 'buff_speed' && `+Speed`}
                                    {move.effect.type === 'buff_attack' && `+Attack`}
                                    {move.effect.type === 'buff_defense' && `+Defense`}
                                    {move.effect.type === 'buff_instinct' && `+Instinct`}
                                    {move.effect.type === 'buff_attack_defense' && `+Atk/Def`}
                                    {move.effect.type === 'buff_attack_speed' && `+Atk/Spd`}
                                    {move.effect.type === 'debuff_attack' && `-Attack`}
                                    {move.effect.type === 'debuff_defense' && `-Defense`}
                                    {move.effect.type === 'debuff_speed' && `-Speed`}
                                    {move.effect.type === 'debuff_instinct' && `-Instinct`}
                                    {move.effect.type === 'debuff_accuracy' && `-Accuracy`}
                                    {move.effect.type === 'debuff_instinct_self' && `Self -Inst`}
                                    {move.effect.type === 'debuff_speed_self' && `Self -Spd`}
                                    {move.effect.type === 'debuff_attack_self' && `Self -Atk`}
                                    {move.effect.type === 'heal_self' && `Heal ${Math.round((move.effect.healPercent || 0.5) * 100)}%`}
                                    {move.effect.type === 'rest' && `Full Heal + Sleep ${move.effect.sleepDuration}t`}
                                    {move.effect.type === 'badly_poison' && `Bad Poison`}
                                    {move.effect.type === 'regen' && `Regen`}
                                    {move.effect.type === 'weather_rain' && `Rain: +50% Water`}
                                    {move.effect.type === 'weather_sun' && `Sun: +50% Fire`}
                                    {move.effect.type === 'weather_sand' && `Sandstorm: Chip DMG`}
                                    {move.effect.type === 'weather_hail' && `Hail: Chip DMG`}
                                    {move.effect.type === 'terrain_electric' && `Terrain: +50% Electric`}
                                    {move.effect.type === 'terrain_grassy' && `Terrain: +50% Grass`}
                                    {move.effect.type === 'terrain_psychic' && `Terrain: +50% Psychic`}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <button
                        onClick={() => startBattle(opponent, false)}
                        disabled={careerData.energy <= 0}
                        className={`tutorial-start-battle w-full rounded-lg py-2.5 transition font-bold text-sm shadow-md ${
                          careerData.energy <= 0
                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                            : 'bg-gradient-to-b from-pokeball-red-light to-pokeball-red text-white hover:from-pokeball-red hover:to-pokeball-red-dark border-2 border-pokeball-red-dark'
                        }`}
                      >
                        {careerData.energy <= 0 ? 'Need Energy!' : 'Battle!'}
                      </button>
                    </div>
                  );
                  })}
                </div>
              </div>
            </motion.div>
          )}

          {/* LOG VIEW */}
          {viewMode === 'log' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="pokeball-lower rounded-2xl p-3 shadow-lg overflow-hidden"
            >
              <div className="relative z-10 pt-1">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-pokeball-black text-lg">Turn Log</h3>
                  <button onClick={() => setViewMode('training')} className="text-sm text-pokeball-black font-bold hover:text-pokeball-red transition px-3 py-1 bg-white rounded-lg border-2 border-pokeball-black/20 hover:border-pokeball-red/50">
                    Back to Training
                  </button>
                </div>
                <div className="space-y-2 max-h-64 sm:max-h-96 overflow-y-auto pocket-scrollbar pr-1">
                  {careerData.turnLog.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-2">📋</div>
                      <p className="text-pokeball-black-light text-sm">No actions yet. Start training!</p>
                    </div>
                  ) : (
                    careerData.turnLog.map((entry, idx) => (
                      <div
                        key={idx}
                        className={`p-2.5 rounded-xl text-sm shadow-sm ${
                          entry.type === 'training_success' ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-600' :
                          entry.type === 'training_levelup' ? 'bg-gradient-to-r from-purple-50 to-violet-50 border-l-4 border-purple-500' :
                          entry.type === 'training_fail' ? 'bg-gradient-to-r from-red-50 to-rose-50 border-l-4 border-pokeball-red' :
                          entry.type === 'battle_victory' || entry.type === 'gym_victory' ? 'bg-gradient-to-r from-blue-50 to-sky-50 border-l-4 border-blue-500' :
                          entry.type === 'battle_loss' ? 'bg-gradient-to-r from-orange-50 to-amber-50 border-l-4 border-orange-500' :
                          'bg-white border-l-4 border-gray-300'
                        }`}
                      >
                        <div className="font-bold text-xs text-pokeball-black-light mb-1">Turn {entry.turn}</div>
                        <div className="text-pokeball-black">{entry.message}</div>
                        {entry.moodLoss && (
                          <div className="text-xs text-orange-600 mt-1 font-medium">
                            Mood: -{entry.moodLoss}
                          </div>
                        )}
                        {entry.moodGain && entry.moodGain > 0 && (
                          <div className="text-xs text-purple-600 mt-1 font-medium">
                            Mood: +{entry.moodGain}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* MODALS */}
      <EvolutionModal />
      <InspirationModal />
      <StrategySelector />
      <PokeclockModal />
      <HelpModal />

      {/* Pokeclock Tutorial Modal - TODO: Reimplement with standard tutorial system */}

      {/* Abandon Career Confirmation Modal */}
      {showAbandonConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
          >
            <div className="text-center mb-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <LogOut size={32} className="text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-pocket-text mb-2">Abandon Career?</h3>
              <p className="text-pocket-text-light text-sm">
                This will permanently end your current career with {careerData?.pokemon?.name || 'your Pokemon'}.
                All progress will be lost and cannot be recovered.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowAbandonConfirm(false)}
                className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl font-bold text-pocket-text transition"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const success = await abandonCareer();
                  if (success) {
                    setShowAbandonConfirm(false);
                    resetGameState();
                    setGameState('menu');
                  }
                }}
                className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 rounded-xl font-bold text-white transition"
              >
                Abandon Career
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Tutorial overlays */}
      {showTutorialOverlay && (
        <TutorialOverlay
          stepId={showTutorialOverlay}
          onContinue={() => {
            const currentStep = showTutorialOverlay;
            setShowTutorialOverlay(null);
            // Advance to next step based on current step
            if (currentStep === 'training-intro') {
              advanceStep('battle-intro');
            } else if (currentStep === 'battle-intro') {
              // Highlight the Battle tab button
              setShowTutorialHighlight('.tutorial-battle-tab');
              advanceStep('battle-select');
            } else if (currentStep === 'battle-select') {
              // Highlight the first Battle! button in the wild battle view
              setShowTutorialHighlight('.tutorial-start-battle');
              advanceStep('mood-intro');
            } else if (currentStep === 'mood-intro') {
              // Highlight the relax button for mood tutorial
              setShowTutorialHighlight('.tutorial-relax-button');
              advanceStep('move-intro');
            } else if (currentStep === 'move-intro') {
              // Highlight the blue book icon to open learn view
              setShowTutorialHighlight('.tutorial-sp-book');
              advanceStep('move-learn');
            } else if (currentStep === 'move-learn') {
              // After move-learn modal, highlight STAB move (same type as Pokemon)
              setShowTutorialHighlight('.tutorial-stab-move');
              advanceStep('rest-intro');
            } else if (currentStep === 'rest-intro') {
              // Highlight the rest button
              setShowTutorialHighlight('.tutorial-rest-button');
              advanceStep('gym-intro');
            } else if (currentStep === 'gym-intro') {
              // After gym-intro, tutorial continues after career ends
              advanceStep('gacha-intro');
            } else if (currentStep === 'pokeclock-first-defeat') {
              // Mark pokeclock first defeat tutorial as shown
              markPokeclockFirstDefeatShown();
              // Transition to battle screen with pending battle state
              if (pendingBattleState) {
                setBattleState(pendingBattleState);
                setPendingBattleState(null);
                setGameState('battle');
              }
            } else if (currentStep === 'pokeclock-usage') {
              // Mark pokeclock usage tutorial as shown
              markPokeclockUsageShown();
              // Transition to battle screen with pending battle state
              if (pendingBattleState) {
                setBattleState(pendingBattleState);
                setPendingBattleState(null);
                setGameState('battle');
              }
            }
          }}
        />
      )}

      {/* Tutorial highlight overlay for interactive elements */}
      <TutorialHighlight
        selector={showTutorialHighlight}
        isActive={!!showTutorialHighlight}
        onTargetClick={() => {
          const currentHighlight = showTutorialHighlight;
          console.log('[Tutorial] onTargetClick called, currentHighlight:', currentHighlight);

          // If clicking the Battle tab, switch to battle view and show battle-select modal
          if (currentHighlight === '.tutorial-battle-tab') {
            setShowTutorialHighlight(null); // Clear first
            setViewMode('battle');
            // Wait for battle view to render, then show the battle-select overlay
            setTimeout(() => {
              setShowTutorialOverlay('battle-select');
              markStepShown('battle-select');
            }, 200);
          } else {
            // Other highlights just get cleared
            setShowTutorialHighlight(null);
          }
        }}
        onDismiss={() => {
          // Clear highlight when clicking outside target
          setShowTutorialHighlight(null);
        }}
      />
    </>
  );
};

export default CareerScreen;
